import { useCallback, useEffect, useRef, useState } from "react";
import { api, ApiError } from "@/api/client";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import {
  CardViewSwitcher,
  cardGridClass,
  useCardView,
  type CardView,
} from "@/components/CardView";
import { useAuth } from "@/auth/AuthContext";
import { Avatar } from "@/components/Avatar";
import { PublishOfferModal, type PublishableHotOffer } from "@/components/PublishOfferModal";
import type {
  FreshAlert,
  FreshAlertListResponse,
  FreshTelegramChat,
  StoredCredential,
} from "@/types";
import {
  FilterChips,
  MenuItem,
  MultiSelectDropdown,
  FilterSection,
  InlineMultiSelect,
  InlineRange,
  PresetChips,
  SegToggle,
  SingleSelectDropdown,
  ToggleRow,
} from "@/components/FilterControls";
import { FiltersDrawer } from "@/components/FiltersDrawer";
import { FreshDeck } from "@/components/FreshDeck";
import { AgentLabelBadge, type AgentLabelFields } from "@/components/AgentLabelBadge";
import { LabelReportDialog } from "@/components/LabelReportDialog";
import {
  useShareLinks,
  SharePrepareModal,
  ShareFromUrlsButton,
  ShareLinkModal,
  ShareEditModal,
  ActivePublicLinksPanel,
  type ShareSelection,
} from "@/components/ShareLinks";

// ─── Wire types (mirror app/schemas/fresh.py) ────────────────────────────
interface FreshPhone {
  number: string;
  is_main: boolean;
  has_viber: boolean;
  has_whatsapp: boolean;
}

interface FreshListing {
  application_id: number;
  source: "ss" | "mh";
  url: string;
  seller_type: string | null;
  // Set when the portal calls this an owner listing but it isn't one.
  // "company": the portal itself links it to a registered firm. "volume": the
  // number posts at agency scale. Such rows are served under the *Agencies*
  // audience, not Owners.
  undeclared_agency: "company" | "volume" | null;
  agency_name: string | null;
  agency_listing_count: number | null;
  deal_type: string | null;
  real_estate_type: string | null;
  commercial_type: string | null;
  city: string | null;
  district: string | null;
  subdistrict: string | null;
  street: string | null;
  address: string | null;
  price_usd: number | null;
  price_geo: number | null;
  unit_price_usd: number | null;
  area: number | null;
  rooms: number | null;
  bedrooms: number | null;
  floor: number | null;
  floors_total: number | null;
  contact_person: string | null;
  phones: FreshPhone[];
  image: string | null;
  image_count: number;
  view_count: number | null;
  vip_status: string | null;
  is_urgent: boolean;
  is_highlighted: boolean;
  badges: string[];
  title: string | null;
  description: string | null;
  create_date: string | null;
  first_seen_at: string | null;
  placement_id: string | null;
  placed_by_user: PlacedByUser | null;
  /** Our last observation of the ad on its portal. "gone" = deleted there
   *  (card greys out, publish is refused); "active" = recently confirmed. */
  liveness: "active" | "gone" | null;
  liveness_checked_at: string | null;
  /** What the owner's text says about agents (app/services/fresh_labels).
   *  null = unlabelled or nothing relevant. */
  agent_label: AgentLabelFields["agent_label"];
  agent_label_confidence: number | null;
  agent_label_reason: string | null;
  agent_label_evidence: string | null;
  agent_label_source: AgentLabelFields["agent_label_source"];
}

interface PlacedByUser {
  id: string;
  full_name: string | null;
  email: string;
  avatar_url: string | null;
}

interface FreshListResponse {
  items: FreshListing[];
  total: number;
  limit: number;
  offset: number;
}

interface Facet {
  value: string;
  count: number;
}

/** One node of the canonical location tree (GET /api/fresh/locations).
 *  ``sources`` says which portals know the place — a value ss.ge has never
 *  heard of will return nothing from ss.ge, and the tag makes that visible
 *  instead of surprising. */
interface LocationNode {
  key: string;
  name_ka: string;
  parent_key: string | null;
  sources: ("ss" | "mh")[];
}

interface QueueStatus {
  pending: number;
  in_progress: number;
  done: number;
  failed: number;
  dead: number;
}

interface FacetsResponse {
  total: number;
  last_24h: number;
  last_1h: number;
  latest_at: string | null;
  deal_types: Facet[];
  real_estate_types: Facet[];
  districts: Facet[];
  cities: Facet[];
  subdistricts: Facet[];
  // Pipeline health per scraper source, e.g. { ss: {...}, mh: {...} }.
  queues: Record<string, QueueStatus>;
}

/** Canonical seller audiences (GET /api/fresh/seller-types). The two portals
 *  label sellers differently, so the backend maps one choice onto each one's
 *  own vocabulary; ``sources`` says where a choice actually exists. */
interface SellerType {
  key: string;
  label: string;
  sources: ("ss" | "mh")[];
}
const DEFAULT_SELLERS = ["owner"];
// Which scraper to read from. MyHome is beta, so ss.ge is the default.
type Source = "ss" | "mh" | "both";
type Sort = "fresh" | "price_desc" | "price_asc" | "views";
type PriceCcy = "usd" | "gel";

const PAGE_SIZE = 40;

// Starting filter state for an agent who hasn't customised yet: both platforms
// and the three main deal types (Sale / Rent / Daily rent). Applied once via a
// versioned migration (see hydration below) so existing agents pick it up too —
// preserving their other saved filters — after which each agent's own source /
// deal-type choices persist. Bump the version to re-push a new initial set.
const INITIAL_FILTERS_VERSION = 3;
const INITIAL_SOURCE: Source = "both";
const INITIAL_DEAL_TYPES = ["იყიდება", "ქირავდება", "ქირავდება დღიურად"];

const SORT_OPTIONS: { value: Sort; label: string }[] = [
  { value: "fresh", label: "Newest" },
  { value: "price_desc", label: "Price ↓" },
  { value: "price_asc", label: "Price ↑" },
  { value: "views", label: "Most viewed" },
];

// Both scraper tables index their date column, so a long window is an index
// scan rather than a table scan (~0.05s for page 1 at 30 days). The exact result
// count is the expensive part and the backend memoises it.
const RECENCY_OPTIONS: { value: string; label: string }[] = [
  { value: "1h", label: "1 hour" },
  { value: "3h", label: "3 hours" },
  { value: "24h", label: "24 hours" },
  { value: "3d", label: "3 days" },
  { value: "1w", label: "1 week" },
  { value: "2w", label: "2 weeks" },
  { value: "1mo", label: "1 month" },
];
const DEFAULT_RECENCY = "1w";
const RECENCY_KEYS = RECENCY_OPTIONS.map((o) => o.value);
// Windows longer than ss.ge's retention. The scraper keeps ~10 days of ss.ge
// owner rows while MyHome goes back years, so these return a MyHome-heavy mix —
// worth saying out loud rather than letting the feed look broken.
const LONG_WINDOWS = new Set(["2w", "1mo"]);

// Styling for the non-VIP promo/feature badges (ss: Urgent/Highlighted/Bumped;
// mh: Highlighted/Promoted/3D). Unknown labels fall back to a neutral slate pill.
const BADGE_STYLE: Record<string, string> = {
  Urgent: "bg-rose-500 text-white ring-1 ring-rose-300/40",
  Highlighted: "bg-fuchsia-500 text-white ring-1 ring-fuchsia-300/40",
  Bumped: "bg-sky-500 text-white ring-1 ring-sky-300/40",
  Promoted: "bg-violet-500 text-white ring-1 ring-violet-300/40",
  "3D": "bg-teal-500 text-white ring-1 ring-teal-300/40",
};

const PRICE_PRESETS_USD = [
  { label: "< $50k", min: "", max: "50000" },
  { label: "$50–100k", min: "50000", max: "100000" },
  { label: "$100–200k", min: "100000", max: "200000" },
  { label: "$200k+", min: "200000", max: "" },
];
const PRICE_PRESETS_GEL = [
  { label: "< ₾150k", min: "", max: "150000" },
  { label: "₾150–300k", min: "150000", max: "300000" },
  { label: "₾300–500k", min: "300000", max: "500000" },
  { label: "₾500k+", min: "500000", max: "" },
];
const AREA_PRESETS = [
  { label: "< 40 m²", min: "", max: "40" },
  { label: "40–70", min: "40", max: "70" },
  { label: "70–120", min: "70", max: "120" },
  { label: "120 m²+", min: "120", max: "" },
];
const ROOMS_PRESETS = [
  { label: "1+", min: "1", max: "" },
  { label: "2+", min: "2", max: "" },
  { label: "3+", min: "3", max: "" },
  { label: "4+", min: "4", max: "" },
];

/** Short trigger label for a min/max range, e.g. "$100k–200k", "2+ rm". */
function rangeLabel(
  base: string,
  min: string,
  max: string,
  fmt: (v: string) => string,
): string {
  if (min && max) return `${fmt(min)}–${fmt(max)}`;
  if (min) return `${fmt(min)}+ ${base}`;
  if (max) return `≤${fmt(max)} ${base}`;
  return base;
}

function compactNum(v: string): string {
  const n = Number(v);
  if (!Number.isFinite(n)) return v;
  if (n >= 1000) return `${n / 1000}k`;
  return String(n);
}

/** Secondary filters tucked into the "More" popover. */
interface AdvFilters {
  bedroomsMin: string;
  bedroomsMax: string;
  floorMin: string;
  floorMax: string;
  floorsTotalMin: string;
  floorsTotalMax: string;
  city: string[];
  subdistrict: string[];
  photosMin: string;
  createdWithin: string;
  isUrgent: boolean;
  isHighlighted: boolean;
  vipOnly: boolean;
}

const EMPTY_ADV: AdvFilters = {
  bedroomsMin: "",
  bedroomsMax: "",
  floorMin: "",
  floorMax: "",
  floorsTotalMin: "",
  floorsTotalMax: "",
  city: [],
  subdistrict: [],
  photosMin: "",
  createdWithin: DEFAULT_RECENCY,
  isUrgent: false,
  isHighlighted: false,
  vipOnly: false,
};

/** How many advanced filters are set — drives the "More (N)" badge. */
function advCount(a: AdvFilters): number {
  let n = 0;
  if (a.bedroomsMin || a.bedroomsMax) n++;
  if (a.floorMin || a.floorMax) n++;
  if (a.floorsTotalMin || a.floorsTotalMax) n++;
  if (a.city.length) n++;
  if (a.subdistrict.length) n++;
  if (a.photosMin) n++;
  if (a.createdWithin && a.createdWithin !== DEFAULT_RECENCY) n++;
  if (a.isUrgent) n++;
  if (a.isHighlighted) n++;
  if (a.vipOnly) n++;
  return n;
}

// ss.ge stores deal / property type in Georgian. Show an English label on the
// pill with the original Georgian kept in the tooltip, and colour the deal pill
// so rent vs sale is legible at a glance.
const DEAL_LABELS: Record<string, { en: string; cls: string }> = {
  "ქირავდება": { en: "Rent", cls: "border-sky-400/40 bg-sky-500/15 text-sky-200" },
  "იყიდება": { en: "Sale", cls: "border-emerald-400/40 bg-emerald-500/15 text-emerald-200" },
  "ქირავდება დღიურად": { en: "Daily", cls: "border-violet-400/40 bg-violet-500/15 text-violet-200" },
  "გირავდება": { en: "Mortgage", cls: "border-amber-400/40 bg-amber-500/15 text-amber-200" },
};
const TYPE_LABELS: Record<string, string> = {
  "ბინა": "Apartment",
  "კომერციული": "Commercial",
  "კერძო სახლი": "House",
  "მიწის ნაკვეთი": "Land",
  "სასტუმრო": "Hotel",
  "აგარაკი": "Cottage",
};

// The Fresh filter selection we remember per-user (persisted server-side and
// auto-restored). Mirrors the buildQuery inputs; cardView stays on its own hook.
interface FiltersBlob {
  sellers?: string[];
  /** Legacy single audience, migrated to ``sellers`` on load. */
  seller?: string;
  source: Source;
  // Which initial-defaults version this saved blob has seen (see INITIAL_*).
  initialVersion?: number;
  q: string;
  sort: Sort;
  dealType: string[];
  realEstateType: string[];
  district: string[];
  locDistricts?: string[];
  locSubdistricts?: string[];
  locStreets?: string[];
  roomsMin: string;
  roomsMax: string;
  priceCcy: PriceCcy;
  priceMin: string;
  priceMax: string;
  areaMin: string;
  areaMax: string;
  adv: AdvFilters;
}

// ── Toolbar sizing ──────────────────────────────────────────────────────────
// Every toolbar control shares the h-9 grid (SegToggle / dropdowns / view
// switcher are h-9 too), so the rows line up pixel-for-pixel. Primaries are
// a notch bolder; secondaries are plain and only tint when active.
const TB_PRIMARY =
  "inline-flex items-center gap-2 h-9 px-3.5 flex-shrink-0 rounded-lg border text-[13px] font-semibold whitespace-nowrap transition-all";
const TB_BTN =
  "inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border text-[12px] font-medium whitespace-nowrap transition-colors";
const TB_BTN_IDLE =
  "text-slate-300 border-white/10 bg-ink-900/60 hover:border-white/20 hover:text-slate-100";
const TB_BTN_ACTIVE = "text-accent-100 bg-accent-500/15 border-accent-500/40";
const TB_BADGE =
  "inline-flex items-center justify-center min-w-[1.15rem] h-[1.15rem] px-1 rounded-full bg-accent-500 text-white text-[11px] font-bold tabular-nums";

export function Fresh() {
  const [sellers, setSellers] = useState<string[]>(DEFAULT_SELLERS);
  const [sellerTypes, setSellerTypes] = useState<SellerType[]>([]);
  useEffect(() => {
    api<{ items: SellerType[] }>("/api/fresh/seller-types")
      .then((r) => setSellerTypes(r.items))
      .catch(() => setSellerTypes([]));
  }, []);
  // Data source toggle. Initial = both platforms (see INITIAL_SOURCE).
  const [source, setSource] = useState<Source>(INITIAL_SOURCE);
  const [q, setQ] = useState<string>("");
  const [view, setView] = useState<"list" | "deck">(
    () => (localStorage.getItem("agencygrid:fresh-view") === "deck" ? "deck" : "list"),
  );
  const setViewPersist = (v: "list" | "deck") => {
    setView(v);
    localStorage.setItem("agencygrid:fresh-view", v);
  };
  const [sort, setSort] = useState<Sort>("fresh");
  const [cardView, setCardView] = useCardView();
  const [filtersOpen, setFiltersOpen] = useState(false);

  // ── Prepare to share: pick listings → public buyer brochure ─────────────
  const share = useShareLinks();
  // Link currently open in the edit modal (add/remove/reorder cards, fix
  // details) — null when closed.
  const [editLinkId, setEditLinkId] = useState<string | null>(null);
  const [prepareMode, setPrepareMode] = useState(false);
  const [shareSelected, setShareSelected] = useState<Set<string>>(new Set());
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  // Same dialog, no selection: the broker pastes listing URLs they already
  // have (forwarded by a colleague, copied from chat) and gets the same link.
  const [urlShareOpen, setUrlShareOpen] = useState(false);
  const toggleShareSelect = (key: string) =>
    setShareSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const exitPrepare = () => {
    setPrepareMode(false);
    setShareSelected(new Set());
    setShareDialogOpen(false);
  };

  // Claim → publish-through-the-upload-engine. Mirrors Owner listings: the
  // "Claim" button opens the shared PublishOfferModal (POST /api/run). If a
  // teammate already published the listing we confirm first (warn, then allow).
  const { user } = useAuth();
  const [publishing, setPublishing] = useState<FreshListing | null>(null);
  const [confirmClaim, setConfirmClaim] = useState<FreshListing | null>(null);
  const [hasSsCreds, setHasSsCreds] = useState(false);
  useEffect(() => {
    api<StoredCredential[]>("/api/credentials")
      .then((rows) => setHasSsCreds(rows.some((r) => r.platform === "ss")))
      .catch(() => setHasSsCreds(false));
  }, []);

  // Primary (always-visible) filters.
  const [dealType, setDealType] = useState<string[]>(INITIAL_DEAL_TYPES);
  const [realEstateType, setRealEstateType] = useState<string[]>([]);
  const [district, setDistrict] = useState<string[]>([]);
  const [roomsMin, setRoomsMin] = useState("");
  const [roomsMax, setRoomsMax] = useState("");
  const [priceCcy, setPriceCcy] = useState<PriceCcy>("usd");
  const [priceMin, setPriceMin] = useState("");
  const [priceMax, setPriceMax] = useState("");
  const [areaMin, setAreaMin] = useState("");
  const [areaMax, setAreaMax] = useState("");

  // ── Location cascade (district → subdistrict → street) ────────────────
  // Each level lists the children of everything checked above it, so the
  // picker only ever offers places that exist under the current selection.
  const [locDistricts, setLocDistricts] = useState<string[]>([]);
  const [locSubdistricts, setLocSubdistricts] = useState<string[]>([]);
  const [locStreets, setLocStreets] = useState<string[]>([]);
  const [districtNodes, setDistrictNodes] = useState<LocationNode[]>([]);
  const [subdistrictNodes, setSubdistrictNodes] = useState<LocationNode[]>([]);
  const [streetNodes, setStreetNodes] = useState<LocationNode[]>([]);

  useEffect(() => {
    api<{ items: LocationNode[] }>("/api/fresh/locations?level=district")
      .then((r) => setDistrictNodes(r.items))
      .catch(() => setDistrictNodes([]));
  }, []);

  // Children of every checked parent, merged. The endpoint reads only our own
  // catalog tables and is memoised server-side, so fanning out is cheap.
  useEffect(() => {
    if (locDistricts.length === 0) {
      setSubdistrictNodes([]);
      return;
    }
    let alive = true;
    Promise.all(
      locDistricts.map((k) =>
        api<{ items: LocationNode[] }>(
          `/api/fresh/locations?level=subdistrict&parent=${encodeURIComponent(k)}`,
        )
          .then((r) => r.items)
          .catch(() => []),
      ),
    ).then((lists) => alive && setSubdistrictNodes(lists.flat()));
    return () => {
      alive = false;
    };
  }, [locDistricts.join(",")]);

  useEffect(() => {
    if (locSubdistricts.length === 0) {
      setStreetNodes([]);
      return;
    }
    let alive = true;
    Promise.all(
      locSubdistricts.map((k) =>
        api<{ items: LocationNode[] }>(
          `/api/fresh/locations?level=street&parent=${encodeURIComponent(k)}`,
        )
          .then((r) => r.items)
          .catch(() => []),
      ),
    ).then((lists) => alive && setStreetNodes(lists.flat()));
    return () => {
      alive = false;
    };
  }, [locSubdistricts.join(",")]);

  // Drop selections whose parent is no longer checked, so the query can't keep
  // filtering by a street the user can't see any more.
  useEffect(() => {
    const visible = new Set(subdistrictNodes.map((n) => n.key));
    setLocSubdistricts((prev) => prev.filter((k) => visible.has(k)));
  }, [subdistrictNodes]);
  useEffect(() => {
    const visible = new Set(streetNodes.map((n) => n.key));
    setLocStreets((prev) => prev.filter((k) => visible.has(k)));
  }, [streetNodes]);

  // Secondary filters, grouped so the "More" popover takes one prop bundle.
  const [adv, setAdv] = useState<AdvFilters>(EMPTY_ADV);
  const patchAdv = useCallback(
    (p: Partial<AdvFilters>) => setAdv((a) => ({ ...a, ...p })),
    [],
  );

  const [facets, setFacets] = useState<FacetsResponse | null>(null);
  const [items, setItems] = useState<FreshListing[]>([]);
  // "Wrong label?" dialog target (agent-posture label correction).
  const [reportTarget, setReportTarget] = useState<FreshListing | null>(null);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Debounce the free-text box so we don't fire a query per keystroke.
  const [qDebounced, setQDebounced] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setQDebounced(q.trim()), 350);
    return () => clearTimeout(t);
  }, [q]);

  // ── Remembered filters (per-user, platform-wide) ────────────────────────
  // Hydrate the filter state once from the saved blob on /api/auth/me, then
  // auto-persist (debounced) on every change so the last-used filters follow
  // the broker across devices/sessions. No manual "save".
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (hydratedRef.current || !user) return;
    hydratedRef.current = true;
    const f = user.fresh_filters as Partial<FiltersBlob> | null;
    if (!f) return;
    // One-time initial push: agents from before the new initial (no/old
    // initialVersion) get "both" + the 3 deal types once; their OTHER saved
    // filters are preserved. Once the version is stamped, their own source /
    // deal-type choices win again.
    const seenInitial = (f.initialVersion ?? 0) >= INITIAL_FILTERS_VERSION;
    // Migrate the pre-multi-select blob: "independent" meant owners + solo
    // brokers. Anything else (or nothing) starts at owners only.
    if (Array.isArray(f.sellers) && f.sellers.length) setSellers(f.sellers);
    else if (f.seller === "independent") setSellers(["owner", "broker"]);
    if (seenInitial && (f.source === "ss" || f.source === "mh" || f.source === "both"))
      setSource(f.source);
    if (typeof f.q === "string") setQ(f.q);
    if (f.sort) setSort(f.sort);
    if (seenInitial && Array.isArray(f.dealType)) setDealType(f.dealType);
    if (Array.isArray(f.realEstateType)) setRealEstateType(f.realEstateType);
    if (Array.isArray(f.district)) setDistrict(f.district);
    if (Array.isArray(f.locDistricts)) setLocDistricts(f.locDistricts);
    if (Array.isArray(f.locSubdistricts)) setLocSubdistricts(f.locSubdistricts);
    if (Array.isArray(f.locStreets)) setLocStreets(f.locStreets);
    if (typeof f.roomsMin === "string") setRoomsMin(f.roomsMin);
    if (typeof f.roomsMax === "string") setRoomsMax(f.roomsMax);
    if (f.priceCcy === "usd" || f.priceCcy === "gel") setPriceCcy(f.priceCcy);
    if (typeof f.priceMin === "string") setPriceMin(f.priceMin);
    if (typeof f.priceMax === "string") setPriceMax(f.priceMax);
    if (typeof f.areaMin === "string") setAreaMin(f.areaMin);
    if (typeof f.areaMax === "string") setAreaMax(f.areaMax);
    if (f.adv && typeof f.adv === "object")
      setAdv({
        ...EMPTY_ADV,
        ...f.adv,
        // The saved blob predates the 1-week default; take the new one once.
        createdWithin: seenInitial ? f.adv.createdWithin ?? DEFAULT_RECENCY : DEFAULT_RECENCY,
      });
  }, [user]);

  useEffect(() => {
    if (!hydratedRef.current) return; // never overwrite saved with pre-hydrate defaults
    const blob: FiltersBlob = {
      sellers, source, initialVersion: INITIAL_FILTERS_VERSION,
      q, sort, dealType, realEstateType, district,
      locDistricts, locSubdistricts, locStreets,
      roomsMin, roomsMax, priceCcy, priceMin, priceMax, areaMin, areaMax, adv,
    };
    const t = setTimeout(() => {
      // Fire-and-forget: local state is the source of truth, no /me refetch.
      void api("/api/auth/me/fresh-filters", { method: "PUT", json: { filters: blob } }).catch(
        () => {},
      );
    }, 500);
    return () => clearTimeout(t);
  }, [
    sellers, source, q, sort, dealType, realEstateType, district,
    locDistricts, locSubdistricts, locStreets,
    roomsMin, roomsMax, priceCcy, priceMin, priceMax, areaMin, areaMax, adv,
  ]);

  // Facets follow the seller toggle only — the headline counts and filter
  // options describe the whole owner-direct pool, not the active filter.
  useEffect(() => {
    let alive = true;
    api<FacetsResponse>(
      `/api/fresh/facets?source=${source}&${sellers.map((v) => `seller_type=${v}`).join("&")}`,
    )
      .then((r) => alive && setFacets(r))
      .catch(() => alive && setFacets(null));
    return () => {
      alive = false;
    };
  }, [sellers.join(","), source]);

  const buildQuery = useCallback(
    (off: number) => {
      const p = new URLSearchParams();
      sellers.forEach((v) => p.append("seller_type", v));
      p.set("source", source);
      p.set("sort", sort);
      p.set("limit", String(PAGE_SIZE));
      p.set("offset", String(off));
      // Multi-value categorical filters → repeated params.
      dealType.forEach((v) => p.append("deal_type", v));
      realEstateType.forEach((v) => p.append("real_estate_type", v));
      district.forEach((v) => p.append("district", v));
      adv.city.forEach((v) => p.append("city", v));
      adv.subdistrict.forEach((v) => p.append("subdistrict", v));
      [...locDistricts, ...locSubdistricts, ...locStreets].forEach((k) =>
        p.append("location", k),
      );
      if (qDebounced) p.set("q", qDebounced);
      p.set("price_ccy", priceCcy);
      if (priceMin) p.set("price_min", priceMin);
      if (priceMax) p.set("price_max", priceMax);
      if (roomsMin) p.set("rooms_min", roomsMin);
      if (roomsMax) p.set("rooms_max", roomsMax);
      if (areaMin) p.set("area_min", areaMin);
      if (areaMax) p.set("area_max", areaMax);
      if (adv.bedroomsMin) p.set("bedrooms_min", adv.bedroomsMin);
      if (adv.bedroomsMax) p.set("bedrooms_max", adv.bedroomsMax);
      if (adv.floorMin) p.set("floor_min", adv.floorMin);
      if (adv.floorMax) p.set("floor_max", adv.floorMax);
      if (adv.floorsTotalMin) p.set("floors_total_min", adv.floorsTotalMin);
      if (adv.floorsTotalMax) p.set("floors_total_max", adv.floorsTotalMax);
      if (adv.photosMin) p.set("photos_min", adv.photosMin);
      // Always bound the window (default 3h, capped 24h) — the backend enforces
      // this too, but sending a valid value keeps stale saved filters in check.
      const cw = RECENCY_KEYS.includes(adv.createdWithin) ? adv.createdWithin : DEFAULT_RECENCY;
      p.set("created_within", cw);
      if (adv.isUrgent) p.set("is_urgent", "true");
      if (adv.isHighlighted) p.set("is_highlighted", "true");
      if (adv.vipOnly) p.set("vip_only", "true");
      return p.toString();
    },
    [
      sellers.join(","),
      source,
      sort,
      dealType,
      realEstateType,
      district,
      qDebounced,
      locDistricts.join(","),
      locSubdistricts.join(","),
      locStreets.join(","),
      priceCcy,
      priceMin,
      priceMax,
      roomsMin,
      roomsMax,
      areaMin,
      areaMax,
      adv,
    ],
  );

  // Reset to page 1 whenever any filter changes.
  useEffect(() => {
    let alive = true;
    setLoading(true);
    setErr(null);
    api<FreshListResponse>(`/api/fresh/listings?${buildQuery(0)}`)
      .then((r) => {
        if (!alive) return;
        setItems(r.items);
        setTotal(r.total);
        setOffset(r.items.length);
      })
      .catch((ex) => {
        if (!alive) return;
        setErr(ex instanceof ApiError ? ex.message : "Failed to load fresh listings.");
        setItems([]);
        setTotal(0);
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [buildQuery]);

  // Live refresh — fresh listings only matter if they're actually fresh.
  // Re-pull page 1 + facets every 25s while the tab is visible, but only
  // when the user is at the top of the feed (offset === first page) so we
  // don't yank a "load more" reading position out from under them.
  const atFirstPage = offset <= PAGE_SIZE;
  const buildQueryRef = useRef(buildQuery);
  buildQueryRef.current = buildQuery;
  useEffect(() => {
    if (!atFirstPage) return;
    const tick = () => {
      if (document.hidden) return;
      api<FreshListResponse>(`/api/fresh/listings?${buildQueryRef.current(0)}`)
        .then((r) => {
          setItems(r.items);
          setTotal(r.total);
          setOffset(r.items.length);
        })
        .catch(() => {});
      api<FacetsResponse>(
      `/api/fresh/facets?source=${source}&${sellers.map((v) => `seller_type=${v}`).join("&")}`,
    )
        .then(setFacets)
        .catch(() => {});
    };
    const id = setInterval(tick, 25_000);
    return () => clearInterval(id);
  }, [atFirstPage, sellers.join(","), source]);

  async function loadMore() {
    if (loadingMore || items.length >= total) return;
    setLoadingMore(true);
    try {
      const r = await api<FreshListResponse>(`/api/fresh/listings?${buildQuery(offset)}`);
      setItems((prev) => [...prev, ...r.items]);
      setTotal(r.total);
      setOffset((o) => o + r.items.length);
    } catch {
      /* keep what we have; the banner already shows on hard failures */
    } finally {
      setLoadingMore(false);
    }
  }

  function clearFilters() {
    setLocDistricts([]);
    setLocSubdistricts([]);
    setLocStreets([]);
    setDealType([]);
    setRealEstateType([]);
    setDistrict([]);
    setQ("");
    setPriceMin("");
    setPriceMax("");
    setRoomsMin("");
    setRoomsMax("");
    setAreaMin("");
    setAreaMax("");
    setAdv(EMPTY_ADV);
  }

  // Claim handlers: warn if already published by a teammate, else go straight
  // to the publish modal.
  function handleClaim(l: FreshListing) {
    if (l.liveness === "gone") return; // card is greyed out; nothing to publish
    if (l.placed_by_user) setConfirmClaim(l);
    else setPublishing(l);
  }
  // The publish endpoint asked the portal and the ad is gone: grey the card
  // out right away (the feed would show the same on its next load).
  function handleListingInactive(l: FreshListing) {
    setItems((prev) =>
      prev.map((it) =>
        it.application_id === l.application_id && it.source === l.source
          ? { ...it, liveness: "gone", liveness_checked_at: new Date().toISOString() }
          : it,
      ),
    );
  }
  function handlePublished(l: FreshListing) {
    // Optimistically light up the "already published" avatar with the current
    // user so the card reflects the claim immediately (no refetch needed).
    if (user) {
      const me: PlacedByUser = {
        id: user.id,
        full_name: user.full_name,
        email: user.email,
        avatar_url: user.avatar_url,
      };
      setItems((prev) =>
        prev.map((it) =>
          it.application_id === l.application_id && !it.placed_by_user
            ? { ...it, placed_by_user: me }
            : it,
        ),
      );
    }
    setPublishing(null);
  }

  // Removable chips for the currently-applied primary filters (advanced set is
  // collapsed into one "+N advanced" chip that clears the whole More popover).
  const chips: { key: string; label: string; onRemove: () => void }[] = [];
  if (qDebounced) chips.push({ key: "q", label: `“${qDebounced}”`, onRemove: () => setQ("") });
  dealType.forEach((v) =>
    chips.push({
      key: `deal:${v}`,
      label: DEAL_LABELS[v]?.en ?? v,
      onRemove: () => setDealType(dealType.filter((x) => x !== v)),
    }),
  );
  realEstateType.forEach((v) =>
    chips.push({
      key: `type:${v}`,
      label: TYPE_LABELS[v] ?? v,
      onRemove: () => setRealEstateType(realEstateType.filter((x) => x !== v)),
    }),
  );
  district.forEach((v) =>
    chips.push({ key: `dist:${v}`, label: v, onRemove: () => setDistrict(district.filter((x) => x !== v)) }),
  );
  const locChip = (
    keys: string[],
    nodes: LocationNode[],
    clear: (v: string[]) => void,
    prefix: string,
  ) =>
    keys.forEach((k) =>
      chips.push({
        key: `${prefix}:${k}`,
        label: nodes.find((n) => n.key === k)?.name_ka ?? k,
        onRemove: () => clear(keys.filter((x) => x !== k)),
      }),
    );
  locChip(locDistricts, districtNodes, setLocDistricts, "locd");
  locChip(locSubdistricts, subdistrictNodes, setLocSubdistricts, "locs");
  locChip(locStreets, streetNodes, setLocStreets, "locst");

  if (roomsMin || roomsMax)
    chips.push({
      key: "rooms",
      label: rangeLabel("rm", roomsMin, roomsMax, (v) => v),
      onRemove: () => {
        setRoomsMin("");
        setRoomsMax("");
      },
    });
  if (priceMin || priceMax)
    chips.push({
      key: "price",
      label: rangeLabel(
        priceCcy === "usd" ? "$" : "₾",
        priceMin,
        priceMax,
        (v) => `${priceCcy === "usd" ? "$" : "₾"}${compactNum(v)}`,
      ),
      onRemove: () => {
        setPriceMin("");
        setPriceMax("");
      },
    });
  if (areaMin || areaMax)
    chips.push({
      key: "area",
      label: rangeLabel("m²", areaMin, areaMax, compactNum),
      onRemove: () => {
        setAreaMin("");
        setAreaMax("");
      },
    });
  if (advCount(adv) > 0)
    chips.push({ key: "adv", label: `+${advCount(adv)} advanced`, onRemove: () => setAdv(EMPTY_ADV) });

  // Active-filter-group count for the Filters button badge. Counts each filter
  // group once (search lives in its own toolbar input, so it's excluded here).
  let filterCount = 0;
  if (dealType.length) filterCount++;
  if (realEstateType.length) filterCount++;
  if (district.length) filterCount++;
  if (locDistricts.length) filterCount++;
  if (locSubdistricts.length) filterCount++;
  if (locStreets.length) filterCount++;
  if (roomsMin || roomsMax) filterCount++;
  if (priceMin || priceMax) filterCount++;
  if (areaMin || areaMax) filterCount++;
  filterCount += advCount(adv);

  // ── Alerts ───────────────────────────────────────────────────────────────
  const [alerts, setAlerts] = useState<FreshAlert[]>([]);
  const [showAlerts, setShowAlerts] = useState(false);
  const [showLinks, setShowLinks] = useState(false);
  const [alertModalOpen, setAlertModalOpen] = useState(false);
  const [editingAlert, setEditingAlert] = useState<FreshAlert | null>(null);
  const [deletingAlert, setDeletingAlert] = useState<FreshAlert | null>(null);

  const loadAlerts = useCallback(async () => {
    try {
      const r = await api<FreshAlertListResponse>("/api/fresh/alerts");
      setAlerts(r.items);
    } catch {
      /* non-fatal — the feed still works without the alert list */
    }
  }, []);
  useEffect(() => {
    loadAlerts();
  }, [loadAlerts]);

  // One Telegram chat per user: saved once (first alert or the "Change"
  // link) and reused by every alert. ``tgChat`` is this page's source of
  // truth; the auth user's copy only seeds the first render.
  const [tgChat, setTgChat] = useState<FreshTelegramChat | null>(null);
  const [tgModalOpen, setTgModalOpen] = useState(false);
  const loadTgChat = useCallback(async () => {
    try {
      setTgChat(await api<FreshTelegramChat>("/api/fresh/alerts/telegram"));
    } catch {
      /* non-fatal — the alert dialog falls back to asking inline */
    }
  }, []);
  useEffect(() => {
    loadTgChat();
  }, [loadTgChat]);
  const tgChatId: string | null = tgChat ? tgChat.chat_id : (user?.telegram_chat_id ?? null);

  /** Serialize the live filter state into a rules blob for the backend
   *  matcher. Omits unset keys and the relative "recency" filter (an alert
   *  only ever fires on brand-new listings, so recency is meaningless). */
  const currentRules = useCallback((): Record<string, unknown> => {
    const r: Record<string, unknown> = { seller_types: sellers, price_ccy: priceCcy };
    const n = (v: string) => Number(v);
    if (q.trim()) r.q = q.trim();
    if (dealType.length) r.deal_type = dealType;
    if (realEstateType.length) r.real_estate_type = realEstateType;
    if (district.length) r.district = district;
    if (adv.city.length) r.city = adv.city;
    if (adv.subdistrict.length) r.subdistrict = adv.subdistrict;
    if (priceMin) r.price_min = n(priceMin);
    if (priceMax) r.price_max = n(priceMax);
    if (roomsMin) r.rooms_min = n(roomsMin);
    if (roomsMax) r.rooms_max = n(roomsMax);
    if (areaMin) r.area_min = n(areaMin);
    if (areaMax) r.area_max = n(areaMax);
    if (adv.bedroomsMin) r.bedrooms_min = n(adv.bedroomsMin);
    if (adv.bedroomsMax) r.bedrooms_max = n(adv.bedroomsMax);
    if (adv.floorMin) r.floor_min = n(adv.floorMin);
    if (adv.floorMax) r.floor_max = n(adv.floorMax);
    if (adv.floorsTotalMin) r.floors_total_min = n(adv.floorsTotalMin);
    if (adv.floorsTotalMax) r.floors_total_max = n(adv.floorsTotalMax);
    if (adv.photosMin) r.photos_min = n(adv.photosMin);
    if (adv.isUrgent) r.is_urgent = true;
    if (adv.isHighlighted) r.is_highlighted = true;
    if (adv.vipOnly) r.vip_only = true;
    return r;
  }, [
    sellers, priceCcy, q, dealType, realEstateType, district, priceMin, priceMax,
    roomsMin, roomsMax, areaMin, areaMax, adv,
  ]);

  /** Load a saved rules blob back into the filter bar (used by "Edit"). */
  function applyRules(r: Record<string, any>) {
    const s = (v: unknown) => (v != null ? String(v) : "");
    setSellers(
      Array.isArray(r.seller_types) && r.seller_types.length
        ? (r.seller_types as string[])
        : r.seller === "independent"
          ? ["owner", "broker"]
          : DEFAULT_SELLERS,
    );
    setPriceCcy(r.price_ccy === "gel" ? "gel" : "usd");
    setQ(r.q ?? "");
    setDealType(r.deal_type ?? []);
    setRealEstateType(r.real_estate_type ?? []);
    setDistrict(r.district ?? []);
    setPriceMin(s(r.price_min));
    setPriceMax(s(r.price_max));
    setRoomsMin(s(r.rooms_min));
    setRoomsMax(s(r.rooms_max));
    setAreaMin(s(r.area_min));
    setAreaMax(s(r.area_max));
    setAdv({
      ...EMPTY_ADV,
      city: r.city ?? [],
      subdistrict: r.subdistrict ?? [],
      bedroomsMin: s(r.bedrooms_min),
      bedroomsMax: s(r.bedrooms_max),
      floorMin: s(r.floor_min),
      floorMax: s(r.floor_max),
      floorsTotalMin: s(r.floors_total_min),
      floorsTotalMax: s(r.floors_total_max),
      photosMin: s(r.photos_min),
      isUrgent: !!r.is_urgent,
      isHighlighted: !!r.is_highlighted,
      vipOnly: !!r.vip_only,
    });
  }

  async function toggleAlert(a: FreshAlert) {
    try {
      await api(`/api/fresh/alerts/${a.id}`, {
        method: "PATCH",
        json: { enabled: !a.enabled },
      });
      await loadAlerts();
    } catch {
      /* ignore */
    }
  }

  if (view === "deck") {
    // FreshDeck renders itself as a fullscreen portal overlay — no wrapper.
    return <FreshDeck onExit={() => setViewPersist("list")} />;
  }

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-6 py-6">
      {/* ── Toolbar (sticks under the top bar on all breakpoints; both the
          desktop topbar and the mobile header are h-14 = 56px). Two rows on
          one 36px (h-9) control grid: row 1 is search + the two primary
          buttons, row 2 is the filter cluster (left) and the action cluster
          (right). Every control is exactly h-9 so neighbours never sit a few
          px apart, and each cluster wraps as a whole group rather than
          spilling single buttons onto a ragged third line. ──── */}
      <div className="sticky top-14 z-20 -mx-3 sm:-mx-6 px-3 sm:px-6 py-2.5 mb-3 bg-ink-950/80 backdrop-blur-md border-b border-white/5 space-y-2">
        {/* Row 1 — search grows, the primaries keep their width */}
        <div className="flex items-center gap-2">
          <div className="relative flex-1 min-w-0">
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.3-4.3" />
            </svg>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search street, title, owner…"
              className="input !h-9 !py-0 !pl-9 !pr-8 !text-[13px] w-full"
            />
            {q && (
              <button
                type="button"
                onClick={() => setQ("")}
                aria-label="Clear search"
                className="absolute right-1.5 top-1/2 -translate-y-1/2 h-6 w-6 flex items-center justify-center rounded-md text-slate-500 hover:text-slate-200 hover:bg-white/5"
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>

          {/* Filters — with an active-group count badge */}
          <button
            type="button"
            onClick={() => setFiltersOpen(true)}
            className={`${TB_PRIMARY} ${
              filterCount > 0
                ? "bg-accent-500/15 text-accent-100 border-accent-500/40"
                : "bg-ink-900/60 text-slate-200 border-white/10 hover:border-white/20"
            }`}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 6h16M7 12h10M10 18h4" />
            </svg>
            Filters
            {filterCount > 0 && <span className={TB_BADGE}>{filterCount}</span>}
          </button>

          {/* Deck mode — Tinder-style review of the fresh owner pool. Glows so
              it reads as the page's headline feature. */}
          <button
            type="button"
            onClick={() => setViewPersist("deck")}
            title="Review deck — swipe through fresh owner listings"
            className={`${TB_PRIMARY} relative text-accent-200 bg-accent-500/10 border-accent-500/40 shadow-[0_0_14px_rgba(90,141,238,0.25)] hover:shadow-[0_0_20px_rgba(90,141,238,0.4)] hover:bg-accent-500/15`}
          >
            <span className="absolute -top-1 -right-1 flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent-400 opacity-60" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-accent-400" />
            </span>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="7" y="4" width="13" height="16" rx="2" />
              <path d="M4 7v13a1 1 0 0 0 1 1h11" opacity=".55" />
            </svg>
            Deck
          </button>

          {/* Alerts + Links live apart from the filter controls: they open
              their own dialogs rather than changing what the feed shows. */}
          <span aria-hidden className="hidden sm:block h-6 w-px bg-white/10 mx-0.5 flex-shrink-0" />
          <button
            type="button"
            onClick={() => setShowAlerts(true)}
            title="Telegram alerts — manage saved filter alerts"
            className={`${TB_BTN} ${TB_BTN_IDLE} flex-shrink-0`}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
              <path d="M13.73 21a2 2 0 0 1-3.46 0" />
            </svg>
            <span className="hidden md:inline">Alerts</span>
            {alerts.length > 0 && <span className={TB_BADGE}>{alerts.length}</span>}
          </button>
          <button
            type="button"
            onClick={() => setShowLinks(true)}
            title="Active public links — brochures you've shared"
            className={`${TB_BTN} ${TB_BTN_IDLE} flex-shrink-0`}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
            </svg>
            <span className="hidden md:inline">Links</span>
            {(share.shareLinks?.length ?? 0) > 0 && (
              <span className={TB_BADGE}>{share.shareLinks!.length}</span>
            )}
          </button>
        </div>

        {/* Row 2 — filter cluster | share actions */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <SegToggle
              options={[
                { value: "ss", label: "ss.ge" },
                { value: "mh", label: "MyHome" },
                { value: "both", label: "Both" },
              ]}
              value={source}
              onChange={(v) => setSource(v as Source)}
            />
            {/* Audience is multi-select now: the portals carry agencies,
                developers and brokers too, and an agent chasing one property
                wants to see who else is on it. Owners stay the default. */}
            <MultiSelectDropdown
              label="Sellers"
              options={sellerTypes.map((t) => ({
                value: t.key,
                label: `${t.label}${t.sources.length === 1 ? ` (${t.sources[0]} only)` : ""}`,
              }))}
              selected={sellers}
              onChange={(next) => setSellers(next.length ? next : DEFAULT_SELLERS)}
              hint="Agencies also covers sellers posting as owners that we've identified as professionals — they carry an orange badge."
            />
            <SingleSelectDropdown
              label="Sort"
              value={sort}
              options={SORT_OPTIONS}
              onChange={(v) => setSort(v as Sort)}
            />
            <CardViewSwitcher value={cardView} onChange={setCardView} />
          </div>

          <div className="flex flex-wrap items-center gap-2 sm:ml-auto">
            <button
              type="button"
              onClick={() => (prepareMode ? exitPrepare() : setPrepareMode(true))}
              title="Pick listings and send a client a public brochure"
              className={`${TB_BTN} ${prepareMode ? TB_BTN_ACTIVE : TB_BTN_IDLE}`}
            >
              {prepareMode ? "Exit share mode" : "📤 Prepare to share"}
            </button>
            <ShareFromUrlsButton
              className="bg-ink-900/60"
              onClick={() => {
                share.setShareError(null);
                setUrlShareOpen(true);
              }}
            />
          </div>
        </div>
      </div>

      {/* Deck promo — the page's headline feature: describe it and pull the
          user in with a soft accent glow. */}
      <button
        type="button"
        onClick={() => setViewPersist("deck")}
        className="group relative w-full overflow-hidden card border border-accent-500/20 bg-gradient-to-r from-accent-500/[0.12] via-accent-500/[0.04] to-transparent px-4 sm:px-5 py-3.5 mb-4 flex items-center gap-3.5 text-left hover:border-accent-500/40 hover:shadow-[0_0_24px_rgba(90,141,238,0.18)] transition-all"
      >
        <span
          aria-hidden
          className="pointer-events-none absolute -left-10 -top-14 h-40 w-40 rounded-full bg-accent-500/20 blur-3xl group-hover:bg-accent-500/30 transition-colors"
        />
        <span className="relative h-10 w-10 rounded-xl bg-accent-500/15 border border-accent-500/30 text-accent-300 flex items-center justify-center flex-shrink-0">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="7" y="4" width="13" height="16" rx="2" />
            <path d="M4 7v13a1 1 0 0 0 1 1h11" opacity=".55" />
          </svg>
        </span>
        <span className="relative min-w-0 flex-1">
          <span className="flex items-center gap-2">
            <span className="text-[13.5px] font-semibold text-slate-100">
              Review deck
            </span>
            <span className="badge border-accent-500/30 bg-accent-500/10 text-accent-300">
              NEW
            </span>
          </span>
          <span className="block text-[11.5px] text-slate-500 truncate">
            Swipe through fresh owner listings — publish to both portals or
            dismiss, one card at a time. Your team never reviews the same
            listing twice.
          </span>
        </span>
        <span className="relative text-[12.5px] font-semibold text-accent-300 whitespace-nowrap group-hover:translate-x-0.5 transition-transform hidden sm:inline">
          Start swiping →
        </span>
      </button>

      {filtersOpen && (
        <FiltersPanel
          onClose={() => setFiltersOpen(false)}
          total={total}
          facets={facets}
          filterCount={filterCount}
          onClear={clearFilters}
          dealType={dealType}
          setDealType={setDealType}
          realEstateType={realEstateType}
          setRealEstateType={setRealEstateType}
          districtNodes={districtNodes}
          subdistrictNodes={subdistrictNodes}
          streetNodes={streetNodes}
          locDistricts={locDistricts}
          locSubdistricts={locSubdistricts}
          locStreets={locStreets}
          setLocDistricts={setLocDistricts}
          setLocSubdistricts={setLocSubdistricts}
          setLocStreets={setLocStreets}
          roomsMin={roomsMin}
          roomsMax={roomsMax}
          setRoomsMin={setRoomsMin}
          setRoomsMax={setRoomsMax}
          priceCcy={priceCcy}
          setPriceCcy={setPriceCcy}
          priceMin={priceMin}
          priceMax={priceMax}
          setPriceMin={setPriceMin}
          setPriceMax={setPriceMax}
          areaMin={areaMin}
          areaMax={areaMax}
          setAreaMin={setAreaMin}
          setAreaMax={setAreaMax}
          adv={adv}
          patchAdv={patchAdv}
        />
      )}

      {/* Active-filter chips — removable, with a clear-all */}
      <FilterChips chips={chips} onClearAll={clearFilters} />

      {showAlerts && (
        <ListModal
          title="Telegram alerts"
          count={alerts.length}
          onClose={() => setShowAlerts(false)}
          actions={
            <button
              type="button"
              onClick={() => {
                setEditingAlert(null);
                setAlertModalOpen(true);
              }}
              title="Save the current filters as a new Telegram alert"
              className={`${TB_BTN} ${TB_BTN_ACTIVE} hover:bg-accent-500/25`}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 5v14M5 12h14" />
              </svg>
              New alert from current filters
            </button>
          }
        >
          <AlertManagerPanel
            embedded
            alerts={alerts}
            chatId={tgChatId}
            botEnabled={tgChat ? tgChat.bot_enabled : true}
            onChangeChat={() => setTgModalOpen(true)}
            onToggle={toggleAlert}
            onTest={async (a) => {
              try {
                await api(`/api/fresh/alerts/${a.id}/test`, { method: "POST" });
                return true;
              } catch {
                return false;
              }
            }}
            onEdit={(a) => {
              applyRules(a.rules as Record<string, any>);
              setEditingAlert(a);
              setAlertModalOpen(true);
            }}
            onDelete={(a) => setDeletingAlert(a)}
          />
        </ListModal>
      )}

      {showLinks && (
        <ListModal
          title="Active public links"
          count={share.shareLinks?.length ?? 0}
          onClose={() => setShowLinks(false)}
          actions={
            <>
              <button
                type="button"
                onClick={() => {
                  setShowLinks(false);
                  setPrepareMode(true);
                }}
                title="Pick listings from the feed and send a client a public brochure"
                className={`${TB_BTN} ${TB_BTN_IDLE}`}
              >
                📤 Prepare to share
              </button>
              <ShareFromUrlsButton
                className="bg-ink-900/60"
                onClick={() => {
                  share.setShareError(null);
                  setUrlShareOpen(true);
                }}
              />
            </>
          }
        >
          <ActivePublicLinksPanel
            embedded
            links={share.shareLinks ?? []}
            onRevoke={share.revoke}
            onRenew={share.renew}
            onEdit={setEditLinkId}
          />
        </ListModal>
      )}

      {alertModalOpen && (
        <AlertModal
          editing={editingAlert}
          rules={currentRules()}
          chatId={tgChatId}
          botUsername={tgChat?.bot_username ?? null}
          onChangeChat={() => setTgModalOpen(true)}
          onClose={() => setAlertModalOpen(false)}
          onSaved={async () => {
            setAlertModalOpen(false);
            // The first alert also saves the chat id as the user's default.
            await Promise.all([loadAlerts(), loadTgChat()]);
          }}
        />
      )}

      {tgModalOpen && (
        <TelegramChatModal
          current={tgChatId}
          botUsername={tgChat?.bot_username ?? null}
          botEnabled={tgChat ? tgChat.bot_enabled : true}
          alertCount={alerts.length}
          onClose={() => setTgModalOpen(false)}
          onSaved={async () => {
            setTgModalOpen(false);
            // Changing the chat rewrites every alert — reload both.
            await Promise.all([loadAlerts(), loadTgChat()]);
          }}
        />
      )}

      <ConfirmDialog
        open={deletingAlert !== null}
        title="Delete this alert?"
        description={
          deletingAlert ? `"${deletingAlert.name}" will stop sending Telegram pings.` : null
        }
        confirmLabel="Delete"
        variant="danger"
        onCancel={() => setDeletingAlert(null)}
        onConfirm={async () => {
          if (!deletingAlert) return;
          try {
            await api(`/api/fresh/alerts/${deletingAlert.id}`, { method: "DELETE" });
          } finally {
            setDeletingAlert(null);
            await loadAlerts();
          }
        }}
      />

      {/* Claim: warn if a teammate already published, then publish */}
      <ConfirmDialog
        open={confirmClaim !== null}
        title="Already published by your team"
        description={
          confirmClaim?.placed_by_user
            ? `${confirmClaim.placed_by_user.full_name || confirmClaim.placed_by_user.email} already published this listing. Publishing again will create a duplicate — continue anyway?`
            : null
        }
        confirmLabel="Publish anyway"
        onCancel={() => setConfirmClaim(null)}
        onConfirm={() => {
          setPublishing(confirmClaim);
          setConfirmClaim(null);
        }}
      />

      {reportTarget && (
        <LabelReportDialog
          source={reportTarget.source}
          externalId={reportTarget.application_id}
          current={reportTarget}
          onClose={() => setReportTarget(null)}
          onSaved={(fields) => {
            // Patch in place so the badge flips immediately; the periodic
            // refresh re-fetches the same values from the server anyway.
            setItems((prev) =>
              prev.map((x) =>
                x.source === reportTarget.source && x.application_id === reportTarget.application_id
                  ? { ...x, ...fields }
                  : x,
              ),
            );
            setReportTarget(null);
          }}
        />
      )}

      {publishing && (
        <PublishOfferModal
          offer={freshToPublishable(publishing)}
          hasSsCreds={hasSsCreds}
          onClose={() => setPublishing(null)}
          onPublished={() => handlePublished(publishing)}
          onListingInactive={() => handleListingInactive(publishing)}
        />
      )}

      {/* Share: collect settings → generate; then show the ready link. */}
      {shareDialogOpen && (
        <SharePrepareModal
          count={shareSelected.size}
          generating={share.generating}
          error={share.shareError}
          onGenerate={async (opts) => {
            const selections: ShareSelection[] = [...shareSelected].map((id) => ({
              kind: "fresh",
              id,
            }));
            const ok = await share.generate(selections, opts);
            if (ok) {
              setShareDialogOpen(false);
              setPrepareMode(false);
              setShareSelected(new Set());
            }
          }}
          onClose={() => setShareDialogOpen(false)}
        />
      )}

      {/* Share from pasted URLs — no page selection involved. */}
      {urlShareOpen && (
        <SharePrepareModal
          count={0}
          urlsOnly
          generating={share.generating}
          error={share.shareError}
          onGenerate={async (opts) => {
            const ok = await share.generate([], opts);
            if (ok) setUrlShareOpen(false);
          }}
          onClose={() => setUrlShareOpen(false)}
        />
      )}
      {share.shareModal && (
        <ShareLinkModal
          link={share.shareModal}
          onClose={() => share.setShareModal(null)}
        />
      )}

      {err && (
        <div className="mb-6 p-3 rounded border border-rose-500/30 bg-rose-500/10 text-rose-200 text-sm">
          {err}
        </div>
      )}

      {/* Share: prepare-mode banner + the agent's active public links */}
      {prepareMode && (
        <div className="card p-3 mb-4 flex items-center gap-3 flex-wrap border-accent-500/30 bg-accent-500/[0.04]">
          <div className="text-sm text-slate-200">
            Pick listings — or paste your own URLs — to send as a public brochure ·{" "}
            <span className="text-accent-300 font-semibold tabular-nums">
              {shareSelected.size}
            </span>{" "}
            selected
          </div>
          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              className="btn-primary text-xs"
              onClick={() => setShareDialogOpen(true)}
            >
              🔗 Generate public URL
            </button>
            <button type="button" className="btn-secondary text-xs" onClick={exitPrepare}>
              Cancel
            </button>
          </div>
        </div>
      )}
      {editLinkId && (
        <ShareEditModal
          linkId={editLinkId}
          onClose={() => setEditLinkId(null)}
          onChanged={share.loadShareLinks}
        />
      )}

      {!err && (
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="text-xs text-slate-500 tabular-nums">
            {loading ? "Loading…" : `${total.toLocaleString()} listing${total === 1 ? "" : "s"}`}
          </div>
        </div>
      )}

      {loading && items.length === 0 ? (
        <div className={cardGridClass(cardView)}>
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="card h-72 animate-pulse bg-white/[0.02]" />
          ))}
        </div>
      ) : items.length === 0 && !err ? (
        <div className="text-center py-20 text-slate-500 text-sm">
          No fresh listings match these filters yet.
        </div>
      ) : (
        <>
          <div className={cardGridClass(cardView)}>
            {items.map((l) => {
              const key = `${l.source}:${l.application_id}`;
              return (
                <FreshCard
                  key={`${l.source}-${l.application_id}`}
                  listing={l}
                  view={cardView}
                  onClaim={handleClaim}
                  onReport={setReportTarget}
                  selectable={prepareMode}
                  selected={shareSelected.has(key)}
                  onToggleSelect={() => toggleShareSelect(key)}
                />
              );
            })}
          </div>
          {items.length < total && (
            <div className="flex justify-center mt-6">
              <button onClick={loadMore} className="btn-secondary" disabled={loadingMore}>
                {loadingMore ? "Loading…" : `Load more (${total - items.length} left)`}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────


/** Unified filters surface — a bottom sheet on mobile, a right-side drawer on
 *  sm+. Composes the shared inline filter primitives and drives the live filter
 *  state directly (no draft state; filters apply as they change). */
function FiltersPanel({
  onClose,
  total,
  facets,
  filterCount,
  onClear,
  dealType,
  setDealType,
  realEstateType,
  setRealEstateType,
  districtNodes,
  subdistrictNodes,
  streetNodes,
  locDistricts,
  locSubdistricts,
  locStreets,
  setLocDistricts,
  setLocSubdistricts,
  setLocStreets,
  roomsMin,
  roomsMax,
  setRoomsMin,
  setRoomsMax,
  priceCcy,
  setPriceCcy,
  priceMin,
  priceMax,
  setPriceMin,
  setPriceMax,
  areaMin,
  areaMax,
  setAreaMin,
  setAreaMax,
  adv,
  patchAdv,
}: {
  onClose: () => void;
  total: number;
  facets: FacetsResponse | null;
  filterCount: number;
  onClear: () => void;
  dealType: string[];
  setDealType: (v: string[]) => void;
  realEstateType: string[];
  setRealEstateType: (v: string[]) => void;
  districtNodes: LocationNode[];
  subdistrictNodes: LocationNode[];
  streetNodes: LocationNode[];
  locDistricts: string[];
  locSubdistricts: string[];
  locStreets: string[];
  setLocDistricts: (v: string[]) => void;
  setLocSubdistricts: (v: string[]) => void;
  setLocStreets: (v: string[]) => void;
  roomsMin: string;
  roomsMax: string;
  setRoomsMin: (v: string) => void;
  setRoomsMax: (v: string) => void;
  priceCcy: PriceCcy;
  setPriceCcy: (v: PriceCcy) => void;
  priceMin: string;
  priceMax: string;
  setPriceMin: (v: string) => void;
  setPriceMax: (v: string) => void;
  areaMin: string;
  areaMax: string;
  setAreaMin: (v: string) => void;
  setAreaMax: (v: string) => void;
  adv: AdvFilters;
  patchAdv: (p: Partial<AdvFilters>) => void;
}) {
  const sym = priceCcy === "usd" ? "$" : "₾";

  return (
    <FiltersDrawer
      onClose={onClose}
      count={filterCount}
      resultTotal={total}
      resultNoun="result"
      onClear={onClear}
    >
          <FilterSection title="Deal type">
            <InlineMultiSelect
              label="Deal type"
              options={facets?.deal_types}
              selected={dealType}
              onChange={setDealType}
            />
          </FilterSection>

          <FilterSection title="Property type">
            <InlineMultiSelect
              label="Property type"
              options={facets?.real_estate_types}
              selected={realEstateType}
              onChange={setRealEstateType}
            />
          </FilterSection>

          <FilterSection
            title="Price"
            aside={
              <div className="inline-flex rounded-md border border-white/10 overflow-hidden text-[11px] font-semibold">
                {(["usd", "gel"] as PriceCcy[]).map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setPriceCcy(c)}
                    className={`px-2.5 py-1 transition ${
                      priceCcy === c ? "bg-white/10 text-slate-100" : "text-slate-500 hover:text-slate-300"
                    }`}
                  >
                    {c === "usd" ? "$ USD" : "₾ GEL"}
                  </button>
                ))}
              </div>
            }
          >
            <InlineRange
              min={priceMin}
              max={priceMax}
              onMin={setPriceMin}
              onMax={setPriceMax}
              prefix={sym}
            />
            <PresetChips
              presets={priceCcy === "usd" ? PRICE_PRESETS_USD : PRICE_PRESETS_GEL}
              min={priceMin}
              max={priceMax}
              onMin={setPriceMin}
              onMax={setPriceMax}
            />
          </FilterSection>

          <FilterSection title="Rooms">
            <InlineRange min={roomsMin} max={roomsMax} onMin={setRoomsMin} onMax={setRoomsMax} />
            <PresetChips
              presets={ROOMS_PRESETS}
              min={roomsMin}
              max={roomsMax}
              onMin={setRoomsMin}
              onMax={setRoomsMax}
            />
          </FilterSection>

          <FilterSection title="Area (m²)">
            <InlineRange min={areaMin} max={areaMax} onMin={setAreaMin} onMax={setAreaMax} suffix="m²" />
            <PresetChips
              presets={AREA_PRESETS}
              min={areaMin}
              max={areaMax}
              onMin={setAreaMin}
              onMax={setAreaMax}
            />
          </FilterSection>

          <FilterSection title="Bedrooms">
            <InlineRange
              min={adv.bedroomsMin}
              max={adv.bedroomsMax}
              onMin={(v) => patchAdv({ bedroomsMin: v })}
              onMax={(v) => patchAdv({ bedroomsMax: v })}
            />
          </FilterSection>

          <FilterSection title="Floor">
            <InlineRange
              min={adv.floorMin}
              max={adv.floorMax}
              onMin={(v) => patchAdv({ floorMin: v })}
              onMax={(v) => patchAdv({ floorMax: v })}
            />
          </FilterSection>

          <FilterSection title="Total floors in building">
            <InlineRange
              min={adv.floorsTotalMin}
              max={adv.floorsTotalMax}
              onMin={(v) => patchAdv({ floorsTotalMin: v })}
              onMax={(v) => patchAdv({ floorsTotalMax: v })}
            />
          </FilterSection>

          <FilterSection title="Min photos">
            <InlineRange
              min={adv.photosMin}
              max=""
              onMin={(v) => patchAdv({ photosMin: v })}
              onMax={() => {}}
            />
          </FilterSection>

          <FilterSection title="City">
            <InlineMultiSelect
              label="City"
              options={facets?.cities}
              selected={adv.city}
              onChange={(v) => patchAdv({ city: v })}
            />
          </FilterSection>

          {/* District → subdistrict → street. Each level lists only the
              children of what's checked above it, and every value is resolved
              to each portal's own vocabulary server-side — so ss.ge and
              MyHome stop filtering each other with the other's spellings. */}
          <FilterSection title="District">
            <LocationPicker
              nodes={districtNodes}
              selected={locDistricts}
              onChange={setLocDistricts}
              placeholder="Search districts…"
            />
          </FilterSection>

          <FilterSection title="Subdistrict">
            <LocationPicker
              nodes={subdistrictNodes}
              selected={locSubdistricts}
              onChange={setLocSubdistricts}
              placeholder="Search subdistricts…"
              empty="Pick a district first."
            />
          </FilterSection>

          <FilterSection title="Street">
            <LocationPicker
              nodes={streetNodes}
              selected={locStreets}
              onChange={setLocStreets}
              placeholder="Search streets…"
              empty="Pick a subdistrict first."
            />
          </FilterSection>

          <FilterSection title="Posted within">
            <div className="flex flex-wrap gap-1.5">
              {RECENCY_OPTIONS.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => patchAdv({ createdWithin: o.value })}
                  className={`h-9 px-3 rounded-lg text-[12px] font-medium border transition ${
                    adv.createdWithin === o.value
                      ? "bg-accent-500/15 text-accent-100 border-accent-500/40"
                      : "border-white/10 text-slate-300 hover:border-white/20"
                  }`}
                >
                  {o.label}
                </button>
              ))}
            </div>
            {LONG_WINDOWS.has(adv.createdWithin) && (
              <p className="text-[11px] text-slate-500 leading-snug">
                ss.ge keeps about ten days of history in our feed — longer
                windows are mostly MyHome.
              </p>
            )}
          </FilterSection>

          <FilterSection title="Flags">
            <ToggleRow
              items={[
                { label: "Urgent only", checked: adv.isUrgent, onChange: (v) => patchAdv({ isUrgent: v }) },
                {
                  label: "Highlighted only",
                  checked: adv.isHighlighted,
                  onChange: (v) => patchAdv({ isHighlighted: v }),
                },
                { label: "VIP only", checked: adv.vipOnly, onChange: (v) => patchAdv({ vipOnly: v }) },
              ]}
            />
          </FilterSection>
    </FiltersDrawer>
  );
}

/** Checklist over one level of the location tree.
 *
 *  Mirrors ``InlineMultiSelect`` but carries the per-portal tag: a place only
 *  ss.ge knows will return nothing from MyHome, and showing that up front beats
 *  an agent wondering why half the feed vanished. */
function LocationPicker({
  nodes,
  selected,
  onChange,
  placeholder,
  empty,
}: {
  nodes: LocationNode[];
  selected: string[];
  onChange: (next: string[]) => void;
  placeholder: string;
  empty?: string;
}) {
  const [query, setQuery] = useState("");
  if (nodes.length === 0) {
    return (
      <div className="px-2.5 py-1.5 text-[12px] text-slate-500">
        {empty ?? "Nothing to choose from."}
      </div>
    );
  }
  const searchable = nodes.length > 6;
  const visible = searchable
    ? nodes.filter((n) => n.name_ka.toLowerCase().includes(query.toLowerCase()))
    : nodes;
  const toggle = (k: string) =>
    onChange(selected.includes(k) ? selected.filter((x) => x !== k) : [...selected, k]);
  return (
    <div>
      {searchable && (
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={placeholder}
          className="input !py-1.5 !text-[12px] mb-1.5"
        />
      )}
      <div className="max-h-40 overflow-y-auto space-y-0.5 pr-0.5">
        {visible.length === 0 && (
          <div className="px-2.5 py-1.5 text-[12px] text-slate-500">No matches</div>
        )}
        {visible.map((n) => (
          <MenuItem key={n.key} checked={selected.includes(n.key)} onClick={() => toggle(n.key)}>
            {n.name_ka}
            <span className="text-slate-600 text-[10px] ml-1.5 uppercase tracking-wide">
              {n.sources.join("·")}
            </span>
          </MenuItem>
        ))}
      </div>
    </div>
  );
}

function FreshCard({
  listing: l,
  view,
  onClaim,
  onReport,
  selectable = false,
  selected = false,
  onToggleSelect,
}: {
  listing: FreshListing;
  view: CardView;
  onClaim: (l: FreshListing) => void;
  /** Opens the "Wrong label?" dialog for this listing (owner rows only). */
  onReport?: (l: FreshListing) => void;
  selectable?: boolean;
  selected?: boolean;
  onToggleSelect?: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const main = l.phones.find((p) => p.is_main) || l.phones[0];
  const age = timeAgo(l.create_date);
  const isNew = minutesSince(l.create_date) !== null && minutesSince(l.create_date)! < 60;
  const isList = view === "list";
  const isCompact = view === "compact";
  const desc = l.description?.trim() || "";
  const showDesc = !isCompact && desc.length > 0;
  const deal = l.deal_type ? DEAL_LABELS[l.deal_type] : undefined;
  const typeEn = l.real_estate_type ? TYPE_LABELS[l.real_estate_type] ?? l.real_estate_type : null;
  const vip = l.vip_status && l.vip_status !== "None" ? l.vip_status : null;
  const gone = l.liveness === "gone";

  const image = (
    <a
      href={l.url}
      target="_blank"
      rel="noreferrer"
      className={`relative block bg-ink-850 overflow-hidden group flex-shrink-0 ${
        isList
          ? "w-32 sm:w-56 self-stretch"
          : isCompact
            ? "aspect-[3/2]"
            : "aspect-[4/3]"
      }`}
    >
      {l.image ? (
        <img
          src={l.image}
          alt={l.title || ""}
          loading="lazy"
          className={`object-cover group-hover:scale-[1.04] transition-transform duration-300 ease-out ${
            isList ? "absolute inset-0 w-full h-full" : "w-full h-full"
          }`}
        />
      ) : (
        <div className="w-full h-full min-h-[6rem] flex items-center justify-center text-slate-600 text-xs">
          no photo
        </div>
      )}
      {/* Subtle top gradient so badges stay legible over bright photos */}
      <div className="absolute inset-x-0 top-0 h-12 bg-gradient-to-b from-black/40 to-transparent pointer-events-none" />
      <div className="absolute top-2 left-2 flex flex-wrap gap-1">
        <span
          className={`inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide shadow-sm ${
            l.source === "mh"
              ? "bg-sky-500 text-white ring-1 ring-sky-300/40"
              : "bg-slate-700 text-slate-100 ring-1 ring-white/10"
          }`}
        >
          {l.source === "mh" ? "MyHome" : "ss.ge"}
        </span>
        {gone && (
          <span
            className="inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide bg-rose-600 text-white shadow-sm ring-1 ring-rose-300/40"
            title={
              l.liveness_checked_at
                ? `No longer on the portal (checked ${timeAgo(l.liveness_checked_at)})`
                : "No longer on the portal"
            }
          >
            Deleted
          </span>
        )}
        {isNew && !gone && (
          <span className="inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide bg-emerald-500 text-white shadow-sm ring-1 ring-emerald-300/40">
            New
          </span>
        )}
        {vip && (
          <span className="inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide bg-amber-400 text-amber-950 shadow-sm ring-1 ring-amber-200/50">
            {vip}
          </span>
        )}
        {/* Our own judgement, not a portal flag — so deliberately outside
            BADGE_STYLE and in a colour none of the portal badges use. */}
        {l.undeclared_agency && (
          <span
            className="inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide bg-orange-500 text-white shadow-sm ring-1 ring-orange-300/40"
            title={
              l.undeclared_agency === "company"
                ? "Listed as an owner, but the portal links this listing to a registered company"
                : `Listed as an owner, but this contact has ${l.agency_listing_count ?? "many"} listings in the last 30 days`
            }
          >
            {l.agency_name ? `Agency · ${l.agency_name}` : "Agency (undeclared)"}
          </span>
        )}
        {/* Agent-posture verdict from the listing text (AI / taught phrase /
            teammate). Tooltip carries the reason and the quoted evidence. */}
        <AgentLabelBadge fields={l} />
        {l.badges.map((b) => (
          <span
            key={b}
            className={`inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide shadow-sm ${
              BADGE_STYLE[b] ?? "bg-slate-600 text-white ring-1 ring-white/10"
            }`}
          >
            {b}
          </span>
        ))}
      </div>
      {l.image_count > 1 && (
        <span className="absolute bottom-2 right-2 inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-semibold text-white bg-black/55 backdrop-blur-sm">
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="5" width="18" height="14" rx="2" />
            <circle cx="9" cy="11" r="2" />
            <path d="m21 15-4-4-9 9" />
          </svg>
          {l.image_count}
        </span>
      )}
      {/* Already published by a teammate → avatar so agents don't duplicate */}
      {l.placed_by_user && (
        <div
          className="absolute top-2 right-2"
          title={`Published by ${l.placed_by_user.full_name || l.placed_by_user.email}`}
        >
          <Avatar
            url={l.placed_by_user.avatar_url}
            name={l.placed_by_user.full_name}
            email={l.placed_by_user.email}
            size="xs"
            className="ring-2 ring-emerald-400/90 shadow-md"
            alt={`Published by ${l.placed_by_user.full_name || l.placed_by_user.email}`}
          />
        </div>
      )}
    </a>
  );

  const body = (
    <div className={`flex flex-col gap-2 flex-1 min-w-0 ${isCompact ? "p-3" : "p-3.5"}`}>
      {/* Price (hero) + deal pill + age */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className={`font-semibold text-slate-100 tabular-nums tracking-tight ${
                isCompact ? "text-[15px]" : "text-lg"
              }`}
            >
              {fmtUsd(l.price_usd)}
            </span>
            {deal ? (
              <span className={`badge ${deal.cls}`} title={l.deal_type || ""}>
                {deal.en}
              </span>
            ) : (
              l.deal_type && <span className="badge">{l.deal_type}</span>
            )}
          </div>
          {(l.price_geo || l.unit_price_usd) && (
            <div className="text-[11px] text-slate-500 tabular-nums mt-0.5">
              {l.price_geo ? `₾${Math.round(l.price_geo).toLocaleString()}` : ""}
              {l.unit_price_usd ? ` · $${Math.round(l.unit_price_usd)}/m²` : ""}
            </div>
          )}
        </div>
        {age && (
          <span className="text-[11px] text-slate-500 whitespace-nowrap flex-shrink-0" title={l.create_date || ""}>
            {age}
          </span>
        )}
      </div>

      {/* Title */}
      {l.title && (
        <div
          className="text-[13px] font-medium text-slate-200 line-clamp-1 leading-snug"
          title={l.title}
        >
          {l.title}
        </div>
      )}

      {/* Spec line — separated with dots for a clean scan */}
      <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-slate-400">
        {[
          typeEn ? <Fact key="type" title={l.real_estate_type || undefined}>{typeEn}</Fact> : null,
          l.area != null ? <Fact key="area">{fmtNum(l.area)} m²</Fact> : null,
          l.rooms != null ? <Fact key="rooms">{l.rooms} rm</Fact> : null,
          l.bedrooms != null ? <Fact key="bd">{l.bedrooms} bd</Fact> : null,
          l.floor != null ? (
            <Fact key="fl">
              fl. {l.floor}
              {l.floors_total != null ? `/${l.floors_total}` : ""}
            </Fact>
          ) : null,
          l.view_count != null ? <Fact key="views">👁 {l.view_count.toLocaleString()}</Fact> : null,
        ]
          .filter(Boolean)
          .map((el, i) => (
            <span key={i} className="flex items-center gap-1.5">
              {i > 0 && <span className="text-slate-700">·</span>}
              {el}
            </span>
          ))}
      </div>

      {/* Location */}
      <div className="flex items-center gap-1 text-xs text-slate-400 min-w-0">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 text-slate-500">
          <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
          <circle cx="12" cy="10" r="3" />
        </svg>
        <span className="truncate" title={locationLine(l)}>{locationLine(l) || "—"}</span>
      </div>

      {/* Description + Read more */}
      {showDesc && (
        <div className="text-xs text-slate-400/90 leading-relaxed">
          <p className={expanded ? "whitespace-pre-line" : "line-clamp-2"}>{desc}</p>
          {desc.length > 90 && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="mt-0.5 text-[11px] font-medium text-accent-300 hover:text-accent-200"
            >
              {expanded ? "Show less" : "Read more"}
            </button>
          )}
        </div>
      )}

      {/* Agent-label feedback: owner rows only (the labeller never reads
          agency listings). Shown even when unlabelled so a missed phrase can
          be taught. Highlighting text on the card first pre-fills the proof. */}
      {onReport && l.seller_type === "owner" && !isCompact && (
        <button
          type="button"
          onClick={() => onReport(l)}
          className="self-start inline-flex items-center gap-1 text-[10.5px] text-slate-500 hover:text-slate-300 transition"
          title={
            l.agent_label
              ? "Correct or confirm the AI's agent label"
              : "Tell the AI what this listing says about agents"
          }
        >
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 22V4a1 1 0 0 1 1-1h11l1 2h3v11h-8l-1-2H5" />
          </svg>
          {l.agent_label ? "Wrong label?" : "Label this listing"}
        </button>
      )}

      <div className="flex-1" />

      {/* Contact */}
      <div className="pt-2.5 mt-0.5 border-t border-white/5 space-y-2.5">
        <div className="min-w-0">
          {l.contact_person && (
            <div className="text-[11px] text-slate-500 truncate">{l.contact_person}</div>
          )}
          {main ? (
            <a
              href={`tel:${main.number}`}
              className="text-sm font-semibold text-accent-300 hover:text-accent-200 tabular-nums flex items-center gap-1.5"
            >
              {main.number}
              {main.has_whatsapp && <WhatsAppIcon className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />}
              {main.has_viber && <ViberIcon className="w-3.5 h-3.5 text-violet-400 flex-shrink-0" />}
            </a>
          ) : (
            <div className="text-xs text-slate-600">no phone</div>
          )}
        </div>
        {/* Actions: claim (publish through the engine) + open on ss.ge */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => onClaim(l)}
            disabled={gone}
            title={
              gone
                ? "This listing is no longer active on the portal"
                : l.placed_by_user
                  ? "Already published by your team — publish anyway"
                  : "Claim & publish to your portals"
            }
            className={`flex-1 inline-flex items-center justify-center min-h-[40px] px-3 text-[13px] rounded-lg font-semibold transition ${
              gone
                ? "bg-white/[0.03] text-slate-500 border border-white/10 cursor-not-allowed"
                : l.placed_by_user
                  ? "bg-white/[0.04] hover:bg-white/[0.08] text-slate-300 border border-white/10"
                  : "bg-accent-600 hover:bg-accent-500 text-white active:scale-[0.98]"
            }`}
          >
            {gone ? "Not active" : l.placed_by_user ? "Publish anyway" : "Claim"}
          </button>
          <a
            href={l.url}
            target="_blank"
            rel="noreferrer"
            title={l.source === "mh" ? "Open on MyHome.ge" : "Open on ss.ge"}
            className="inline-flex items-center justify-center min-h-[40px] px-3 text-[13px] rounded-lg bg-white/[0.04] hover:bg-white/[0.08] text-slate-300 border border-white/10 font-medium whitespace-nowrap"
          >
            {l.source === "mh" ? "MyHome ↗" : "ss.ge ↗"}
          </a>
        </div>
      </div>
    </div>
  );

  return (
    <div
      className={`relative card overflow-hidden hover:border-white/15 hover:shadow-lg hover:shadow-black/20 transition ${
        isList ? "flex flex-row items-stretch" : "flex flex-col"
      } ${selected ? "ring-2 ring-accent-500/70 border-accent-500/40" : ""} ${
        gone ? "opacity-55 grayscale" : ""
      }`}
    >
      {selectable && (
        <button
          type="button"
          onClick={onToggleSelect}
          aria-pressed={selected}
          title={selected ? "Remove from share" : "Add to share"}
          className={`absolute top-2 right-2 z-20 h-7 w-7 rounded-md border flex items-center justify-center transition ${
            selected
              ? "bg-accent-500 border-accent-400 text-white"
              : "bg-black/50 border-white/25 text-white/70 hover:bg-black/70 backdrop-blur-sm"
          }`}
        >
          {selected ? (
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 6 9 17l-5-5" />
            </svg>
          ) : (
            <span className="block h-3.5 w-3.5 rounded-sm border border-current" />
          )}
        </button>
      )}
      {image}
      {body}
    </div>
  );
}

function Fact({ children, title }: { children: React.ReactNode; title?: string }) {
  return (
    <span className="text-slate-400" title={title}>
      {children}
    </span>
  );
}

/** Build the PublishOfferModal input from a Fresh card. ``id: null`` → the
 *  pipeline publishes URL-only (no HotOffer auto-claim); the Placement it writes
 *  still powers the "already published" avatar. */
function freshToPublishable(l: FreshListing): PublishableHotOffer {
  const main = l.phones.find((p) => p.is_main) || l.phones[0];
  return {
    id: null,
    platform: "ss",
    external_id: String(l.application_id),
    published_url: l.url,
    title: l.title,
    short_description: l.description,
    cover_photo_url: l.image,
    price: l.price_usd != null ? String(Math.round(l.price_usd)) : null,
    currency: l.price_usd != null ? "USD" : null,
    district: l.district,
    rooms: l.rooms,
    area_sqm: l.area != null ? String(l.area) : null,
    owner_phone: main?.number ?? null,
  };
}

/** WhatsApp brand glyph. ``fill="currentColor"`` → colour from a text-* class. */
function WhatsAppIcon({ className = "" }: { className?: string }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.297-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
      <path d="M20.52 3.449C12.831-3.984.106 1.407.101 11.893c0 2.092.546 4.135 1.585 5.946L0 24l6.335-1.652a11.93 11.93 0 0 0 5.71 1.452h.005c9.846 0 16.067-10.692 11.165-19.13a11.821 11.821 0 0 0-2.696-3.221zm-8.47 18.297h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.999-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.002-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884z" />
    </svg>
  );
}

/** Viber brand glyph (no asset existed in the repo). ``fill="currentColor"`` so
 *  the colour comes from a text-* class on the parent, like WhatsAppIcon. */
function ViberIcon({ className = "" }: { className?: string }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M11.398.002C9.473.028 5.331.344 3.014 2.467 1.294 4.177.693 6.7.623 9.82.554 12.94.482 18.79 5.964 20.34l.004 2.354s-.036.953.594 1.148c.762.235 1.209-.492 1.936-1.276.399-.43.949-1.06 1.365-1.541 3.674.31 6.497-.398 6.818-.502.742-.242 4.943-.78 5.627-6.356.706-5.748-.342-9.384-2.23-11.024l-.002-.001c-.571-.525-2.865-2.19-7.982-2.213 0 0-.603-.038-1.303-.033zm.062 1.591c.596-.004 1.104.033 1.104.033 4.331.017 6.406 1.32 6.891 1.759 1.598 1.37 2.412 4.646 1.812 9.443v.002c-.577 4.657-3.98 4.952-4.608 5.153-.267.086-2.752.703-5.876.5 0 0-2.328 2.81-3.055 3.54-.114.115-.249.16-.336.138-.124-.032-.158-.18-.157-.399l.023-3.885c-4.65-1.284-4.38-6.006-4.325-8.487.055-2.482.518-4.517 1.905-5.888C7.784 1.86 11.15 1.607 11.46 1.593zm.377 2.501a.256.256 0 00-.257.253c0 .142.114.257.256.258 1.277.01 2.331.428 3.181 1.243.85.818 1.269 1.925 1.28 3.397a.256.256 0 00.257.253h.002a.256.256 0 00.253-.258c-.011-1.586-.478-2.847-1.44-3.774-.96-.921-2.17-1.38-3.53-1.39l-.002.001zm-3.514.607a.75.75 0 00-.565.194l-.007.008c-.356.32-.677.677-.95 1.064-.258.367-.398.738-.43 1.101-.019.22.023.436.124.639l.007.016c.288.683.766 1.39 1.459 2.16.687.762 1.418 1.413 2.174 1.936l.016.01.016.011.013.01.016.01c.753.501 1.47.862 2.138 1.075l.005.001c.622.198 1.219.242 1.774.134l.02-.004a2.01 2.01 0 00.842-.45l.013-.011c.368-.324.678-.687.92-1.079.223-.36.295-.715.215-1.055a.82.82 0 00-.375-.502l-.019-.011c-.219-.134-.83-.492-1.252-.716-.438-.232-.83-.153-1.09.194l-.229.305c-.124.166-.334.143-.334.143l-.006-.003c-1.545-.4-1.955-1.964-1.955-1.964s-.023-.212.143-.334l.305-.23c.346-.26.425-.652.194-1.09-.224-.422-.582-1.033-.716-1.252l-.011-.019a.82.82 0 00-.559-.381.79.79 0 00-.083-.009zm3.852.82a.218.218 0 00-.217.22c0 .12.097.218.217.218.893.008 1.608.296 2.183.858.573.562.855 1.296.864 2.24a.218.218 0 00.219.216h.002a.218.218 0 00.216-.22c-.01-1.042-.343-1.906-.944-2.505-.605-.6-1.44-.933-2.537-.943l-.003.001zm.42 1.454a.216.216 0 00-.151.368c.38.382.572.87.582 1.503a.216.216 0 00.216.212h.003a.216.216 0 00.212-.22c-.01-.744-.25-1.368-.716-1.833a.216.216 0 00-.146-.062z" />
    </svg>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────

function locationLine(l: FreshListing): string {
  return [l.district, l.subdistrict, l.street].filter(Boolean).join(", ");
}

function fmtUsd(v: number | null): string {
  if (v == null) return "—";
  return `$${Math.round(v).toLocaleString()}`;
}

function fmtNum(v: number): string {
  return Number.isInteger(v) ? String(v) : v.toFixed(1);
}

function minutesSince(iso: string | null): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return (Date.now() - t) / 60000;
}

function timeAgo(iso: string | null): string | null {
  const mins = minutesSince(iso);
  if (mins == null) return null;
  if (mins < 1) return "just now";
  if (mins < 60) return `${Math.floor(mins)}m ago`;
  const hrs = mins / 60;
  if (hrs < 24) return `${Math.floor(hrs)}h ago`;
  const days = hrs / 24;
  if (days < 30) return `${Math.floor(days)}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

// ─── Alerts ───────────────────────────────────────────────────────────────

/** Short human summary of a saved rules blob for the manage list. */
function summarizeRules(r: Record<string, any>): string {
  const parts: string[] = [];
  const arr = (k: string) =>
    Array.isArray(r[k]) && r[k].length ? (r[k] as string[]).join(", ") : null;
  if (arr("deal_type")) parts.push(arr("deal_type")!);
  if (arr("real_estate_type")) parts.push(arr("real_estate_type")!);
  const sym = r.price_ccy === "gel" ? "₾" : "$";
  if (r.price_min != null || r.price_max != null)
    parts.push(`${sym}${r.price_min ?? ""}–${r.price_max ?? ""}`);
  if (r.rooms_min != null || r.rooms_max != null)
    parts.push(`${r.rooms_min ?? ""}-${r.rooms_max ?? ""} rm`);
  if (r.area_min != null || r.area_max != null)
    parts.push(`${r.area_min ?? ""}-${r.area_max ?? ""} m²`);
  if (arr("district")) parts.push(arr("district")!);
  if (arr("city")) parts.push(arr("city")!);
  if (r.is_urgent) parts.push("urgent");
  if (r.vip_only) parts.push("VIP");
  if (r.q) parts.push(`"${r.q}"`);
  return parts.length ? parts.join(" · ") : "All listings in the selected audience";
}

/** Rule blob → short chips for the alert card (deal, type, price, rooms,
 *  area, location, flags). Mirrors ``summarizeRules`` but as discrete tokens. */
function ruleChips(r: Record<string, any>): Array<{ text: string; tone?: "accent" | "warn" }> {
  const out: Array<{ text: string; tone?: "accent" | "warn" }> = [];
  const arr = (k: string): string[] => (Array.isArray(r[k]) ? (r[k] as string[]) : []);
  const money = (n: number) => {
    const sym = r.price_ccy === "gel" ? "₾" : "$";
    return n >= 10000 ? `${sym}${(n / 1000).toFixed(n % 1000 ? 1 : 0)}k` : `${sym}${n.toLocaleString()}`;
  };
  const range = (min: unknown, max: unknown, fmt: (n: number) => string, unit = "") => {
    const a = min != null ? Number(min) : null;
    const b = max != null ? Number(max) : null;
    if (a != null && b != null) return `${fmt(a)}–${fmt(b)}${unit}`;
    if (a != null) return `${fmt(a)}+${unit}`;
    if (b != null) return `≤ ${fmt(b)}${unit}`;
    return null;
  };
  arr("deal_type").forEach((v) => out.push({ text: v, tone: "accent" }));
  arr("real_estate_type").forEach((v) => out.push({ text: v }));
  const price = range(r.price_min, r.price_max, money);
  if (price) out.push({ text: price });
  const rooms = range(r.rooms_min, r.rooms_max, String, " rm");
  if (rooms) out.push({ text: rooms });
  const beds = range(r.bedrooms_min, r.bedrooms_max, String, " bd");
  if (beds) out.push({ text: beds });
  const area = range(r.area_min, r.area_max, String, " m²");
  if (area) out.push({ text: area });
  const floor = range(r.floor_min, r.floor_max, String, " fl");
  if (floor) out.push({ text: `floor ${floor}` });
  [...arr("district"), ...arr("subdistrict"), ...arr("city")].forEach((v) => out.push({ text: v }));
  const sellers = arr("seller_types");
  if (sellers.length && !(sellers.length === 1 && sellers[0] === "owner")) {
    out.push({ text: sellers.join(" + ") });
  }
  if (r.is_urgent) out.push({ text: "urgent", tone: "warn" });
  if (r.vip_only) out.push({ text: "VIP", tone: "warn" });
  if (r.is_highlighted) out.push({ text: "highlighted", tone: "warn" });
  if (r.photos_min) out.push({ text: `${r.photos_min}+ photos` });
  if (r.q) out.push({ text: `"${String(r.q).length > 28 ? String(r.q).slice(0, 28) + "…" : r.q}"` });
  return out;
}

/** Liveness pill for one alert. The poll loop advances ``last_checked_at``
 *  every ~3 min, so a recent value means the service is alive and watching
 *  this alert; a stale value flags a stalled poller. */
function AlertHeartbeat({ alert }: { alert: FreshAlert }) {
  const checkedMins = minutesSince(alert.last_checked_at);
  // Live if checked within ~2.5 poll intervals (poll is 3 min).
  const live = checkedMins != null && checkedMins < 8;
  if (!alert.enabled) {
    return (
      <span className="badge border-white/10 bg-white/[0.04] text-slate-400 whitespace-nowrap">
        <span className="h-1.5 w-1.5 rounded-full bg-slate-500" />
        Paused
      </span>
    );
  }
  if (checkedMins == null) {
    return (
      <span className="badge border-white/10 bg-white/[0.04] text-slate-400 whitespace-nowrap">
        <span className="h-1.5 w-1.5 rounded-full bg-slate-500" />
        Not run yet
      </span>
    );
  }
  return (
    <span
      className={`badge whitespace-nowrap ${
        live
          ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
          : "border-amber-500/30 bg-amber-500/10 text-amber-300"
      }`}
      title={live ? "Watching — the poller checked this alert recently" : "The poller hasn't checked this alert recently"}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${live ? "bg-emerald-400" : "bg-amber-400"}`} />
      {live ? "Live" : "Stalled"} · checked {timeAgo(alert.last_checked_at)}
    </span>
  );
}

const ALERT_ACTION =
  "inline-flex items-center h-7 px-2 rounded-md text-[11.5px] font-medium whitespace-nowrap transition-colors";

/** Wide dialog for the Alerts / Links managers. Sits at z-45: above the
 *  page chrome (sticky toolbar z-20, topbar z-30, sidebar z-40) but below the
 *  z-50 edit/confirm dialogs those managers open, so they stack on top. */
function ListModal({
  title,
  count,
  actions,
  onClose,
  children,
}: {
  title: string;
  count: number;
  actions?: React.ReactNode;
  onClose: () => void;
  children: React.ReactNode;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);
  return (
    <div
      className="fixed inset-0 z-[45] flex items-start sm:items-center justify-center p-3 sm:p-6 bg-black/65 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
        className="card w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden"
      >
        <div className="flex flex-wrap items-center gap-2 px-4 sm:px-5 py-3 border-b border-white/5">
          <div className="flex items-center gap-2 min-w-0">
            <div className="text-[15px] font-semibold text-slate-100 leading-tight truncate">{title}</div>
            {count > 0 && (
              <span className="badge border-white/10 bg-white/[0.04] text-slate-300 tabular-nums">{count}</span>
            )}
          </div>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            {actions}
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="icon-btn"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        <div className="px-4 sm:px-5 py-4 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}

function AlertManagerPanel({
  embedded = false,
  alerts,
  chatId,
  botEnabled,
  onChangeChat,
  onToggle,
  onTest,
  onEdit,
  onDelete,
}: {
  /** True inside the alerts dialog: no card chrome or section title. */
  embedded?: boolean;
  alerts: FreshAlert[];
  /** The user's saved chat; null = not set yet. */
  chatId: string | null;
  botEnabled: boolean;
  onChangeChat: () => void;
  onToggle: (a: FreshAlert) => void;
  /** Resolves true when the test ping went out. */
  onTest: (a: FreshAlert) => Promise<boolean>;
  onEdit: (a: FreshAlert) => void;
  onDelete: (a: FreshAlert) => void;
}) {
  // Per-alert test feedback, shown inline on the button for a few seconds.
  const [testState, setTestState] = useState<Record<string, "busy" | "ok" | "fail">>({});
  async function runTest(a: FreshAlert) {
    setTestState((st) => ({ ...st, [a.id]: "busy" }));
    const ok = await onTest(a);
    setTestState((st) => ({ ...st, [a.id]: ok ? "ok" : "fail" }));
    window.setTimeout(() => {
      setTestState((st) => {
        const { [a.id]: _dropped, ...rest } = st;
        return rest;
      });
    }, 3500);
  }

  return (
    <div className={embedded ? "space-y-2.5" : "card p-3 mb-5"}>
      <div
        className={`flex flex-wrap items-center gap-x-3 gap-y-1.5 ${
          embedded ? "rounded-lg border border-white/[0.07] bg-ink-850/60 px-3 py-2" : "mb-2"
        }`}
      >
        {!embedded && (
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            Telegram alerts
          </div>
        )}
        {embedded && (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-accent-400 flex-shrink-0">
            <path d="m22 2-7 20-4-9-9-4Z" />
            <path d="M22 2 11 13" />
          </svg>
        )}
        <div className={`flex items-center gap-2 text-[12px] min-w-0 ${embedded ? "" : "sm:ml-auto"}`}>
          {chatId ? (
            <>
              <span className="text-slate-500 whitespace-nowrap">Delivered to chat</span>
              <span className="font-mono text-slate-200 truncate" title={chatId}>
                {chatId}
              </span>
            </>
          ) : (
            <span className="text-amber-300/90">No Telegram chat set yet</span>
          )}
          <button
            type="button"
            onClick={onChangeChat}
            className="text-accent-300 hover:text-accent-200 font-medium whitespace-nowrap"
          >
            {chatId ? "Change" : "Set up"}
          </button>
        </div>
      </div>
      {!botEnabled && (
        <div className="text-[11.5px] text-amber-300/90 bg-amber-500/10 border border-amber-500/20 rounded-md px-2.5 py-1.5 mb-2">
          The alert bot isn't configured on the server (FRESH_ALERT_BOT_TOKEN). Alerts are
          saved, but nothing will be delivered until it is.
        </div>
      )}
      {alerts.length === 0 ? (
        <div className="text-[12.5px] text-slate-500 py-8 text-center rounded-xl border border-dashed border-white/10">
          No alerts yet — set your filters on the page, then use "New alert from current
          filters" to get a Telegram ping when new matching listings appear.
        </div>
      ) : (
        <div className="grid gap-2.5 lg:grid-cols-2">
          {alerts.map((a) => {
            const t = testState[a.id];
            // Only surface the per-alert chat when it differs from the saved
            // default (legacy alerts created before the one-chat model).
            const drifted = chatId != null && a.telegram_chat_id !== chatId;
            const chips = ruleChips(a.rules as Record<string, any>);
            const fired =
              a.last_notified_at != null
                ? `Last sent ${timeAgo(a.last_notified_at)}${
                    a.last_match_count
                      ? ` · ${a.last_match_count} listing${a.last_match_count === 1 ? "" : "s"}`
                      : ""
                  }`
                : "Nothing sent yet";
            return (
              <div
                key={a.id}
                className={`rounded-xl border border-white/[0.07] bg-ink-850/60 p-3 flex flex-col gap-2.5 min-w-0 ${
                  a.enabled ? "" : "opacity-75"
                }`}
              >
                {/* Headline: switch · name · liveness */}
                <div className="flex items-center gap-2.5 min-w-0">
                  <button
                    type="button"
                    role="switch"
                    aria-checked={a.enabled}
                    onClick={() => onToggle(a)}
                    title={a.enabled ? "Enabled — click to pause" : "Paused — click to enable"}
                    className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border transition-colors ${
                      a.enabled
                        ? "bg-emerald-500/80 border-emerald-400/50"
                        : "bg-white/10 border-white/10"
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 left-0.5 h-3.5 w-3.5 rounded-full bg-white shadow transition-transform ${
                        a.enabled ? "translate-x-4" : ""
                      }`}
                    />
                  </button>
                  <div className="min-w-0 flex-1">
                    <div
                      className={`text-[13px] font-semibold truncate ${a.enabled ? "text-slate-100" : "text-slate-400"}`}
                      title={a.name}
                    >
                      {a.name}
                    </div>
                    {drifted && (
                      <div
                        className="text-[10.5px] text-amber-300/80 truncate"
                        title="This alert still points at an older chat id. Edit and save it to move it to your current chat."
                      >
                        chat {a.telegram_chat_id}
                      </div>
                    )}
                  </div>
                  <AlertHeartbeat alert={a} />
                </div>

                {/* Rule chips */}
                <div className="flex flex-wrap gap-1.5 min-w-0">
                  {chips.length === 0 ? (
                    <span className="text-[11.5px] text-slate-500">All listings in the selected audience</span>
                  ) : (
                    chips.map((c, i) => (
                      <span
                        key={i}
                        className={`badge font-medium ${
                          c.tone === "accent"
                            ? "border-accent-500/30 bg-accent-500/10 text-accent-400"
                            : c.tone === "warn"
                              ? "border-amber-500/30 bg-amber-500/10 text-amber-300"
                              : "border-white/10 bg-white/[0.04] text-slate-300"
                        }`}
                      >
                        {c.text}
                      </span>
                    ))
                  )}
                </div>

                {/* Footer: last fire · actions */}
                <div className="flex items-center gap-1 pt-2 border-t border-white/[0.05] min-w-0">
                  <span
                    className={`inline-flex items-center gap-1 text-[11px] truncate ${
                      a.last_notified_at ? "text-slate-400" : "text-slate-500"
                    }`}
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
                      <path d="m22 2-7 20-4-9-9-4Z" />
                      <path d="M22 2 11 13" />
                    </svg>
                    {fired}
                  </span>
                  <div className="ml-auto flex items-center gap-1">
                    <button
                      onClick={() => runTest(a)}
                      disabled={t === "busy"}
                      className={`${ALERT_ACTION} ${
                        t === "ok"
                          ? "text-emerald-300"
                          : t === "fail"
                            ? "text-rose-300"
                            : "text-slate-400 hover:text-slate-100 hover:bg-white/[0.06]"
                      }`}
                    >
                      {t === "busy" ? "Sending…" : t === "ok" ? "Sent ✓" : t === "fail" ? "Failed" : "Test"}
                    </button>
                    <button
                      onClick={() => onEdit(a)}
                      className={`${ALERT_ACTION} text-slate-300 border border-white/10 hover:border-white/20 hover:text-slate-100`}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => onDelete(a)}
                      className={`${ALERT_ACTION} text-rose-300/80 hover:text-rose-200 hover:bg-rose-500/10`}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** The two steps a user needs before the bot can reach them. Shared by the
 *  alert dialog (first-time inline setup) and the chat settings dialog. */
function TelegramHowTo({ botUsername }: { botUsername: string | null }) {
  const link = "text-accent-300 hover:text-accent-200 underline-offset-2 hover:underline";
  return (
    <ol className="list-decimal pl-4 space-y-1 text-[11.5px] leading-snug text-slate-500">
      <li>
        Open{" "}
        {botUsername ? (
          <a href={`https://t.me/${botUsername}`} target="_blank" rel="noreferrer" className={link}>
            @{botUsername}
          </a>
        ) : (
          "the alert bot"
        )}{" "}
        in Telegram and press <span className="text-slate-300">Start</span> — the bot can
        only message chats that messaged it first.
      </li>
      <li>
        Find your chat id: message{" "}
        <a href="https://t.me/userinfobot" target="_blank" rel="noreferrer" className={link}>
          @userinfobot
        </a>{" "}
        and copy the <span className="font-mono text-slate-300">Id</span> it replies with.
        For a group, add the bot to the group and use the group's id (starts with{" "}
        <span className="font-mono text-slate-300">-100</span>).
      </li>
    </ol>
  );
}

function AlertModal({
  editing,
  rules,
  chatId,
  botUsername,
  onChangeChat,
  onClose,
  onSaved,
}: {
  editing: FreshAlert | null;
  rules: Record<string, unknown>;
  /** The user's saved chat; null = first time, ask inline and save it. */
  chatId: string | null;
  botUsername: string | null;
  onChangeChat: () => void;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(editing?.name ?? "");
  // Only asked when nothing is saved yet — the backend then stores it as the
  // user's default so every later alert reuses it silently.
  const [firstChatId, setFirstChatId] = useState("");
  const needsChat = chatId == null;
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // When editing, keep the alert's saved rules; when creating, snapshot the
  // current filter bar.
  const effectiveRules = editing ? (editing.rules as Record<string, unknown>) : rules;

  async function save() {
    if (!name.trim()) {
      setErr("Give the alert a name.");
      return;
    }
    if (needsChat && !firstChatId.trim()) {
      setErr("Enter the Telegram chat id the alerts should go to.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      if (editing) {
        await api(`/api/fresh/alerts/${editing.id}`, {
          method: "PATCH",
          json: {
            name: name.trim(),
            rules: effectiveRules,
            // Re-point a legacy alert at the saved chat on save.
            ...(chatId ? { telegram_chat_id: chatId } : {}),
          },
        });
      } else {
        await api("/api/fresh/alerts", {
          method: "POST",
          json: {
            name: name.trim(),
            rules: effectiveRules,
            ...(needsChat ? { telegram_chat_id: firstChatId.trim() } : {}),
          },
        });
      }
      onSaved();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Save failed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[1300] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-md card p-5 space-y-4">
        <div className="text-sm font-semibold text-slate-100">
          {editing ? "Edit alert" : "Save as alert"}
        </div>
        <div className="space-y-1">
          <label className="text-[11px] uppercase tracking-wider text-slate-500">Name</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Saburtalo 2-room under $80k"
            className="input"
            autoFocus
          />
        </div>
        <div className="space-y-1">
          <label className="text-[11px] uppercase tracking-wider text-slate-500">Filters</label>
          <div className="text-[12px] text-slate-300 bg-white/[0.03] rounded-md px-2.5 py-2">
            {summarizeRules(effectiveRules as Record<string, any>)}
          </div>
          {!editing && (
            <div className="text-[11px] text-slate-500">
              Snapshot of your current filter bar.
            </div>
          )}
        </div>
        <div className="space-y-1.5">
          <label className="text-[11px] uppercase tracking-wider text-slate-500">
            Delivered to
          </label>
          {needsChat ? (
            <>
              <input
                value={firstChatId}
                onChange={(e) => setFirstChatId(e.target.value)}
                placeholder="Telegram chat id, e.g. 123456789"
                className="input font-mono"
                inputMode="numeric"
              />
              <div className="text-[11px] text-slate-500">
                Saved once as your Telegram chat — every alert you create later goes there
                automatically. You can change it any time from the Alerts panel.
              </div>
              <TelegramHowTo botUsername={botUsername} />
            </>
          ) : (
            <div className="flex items-center gap-2 text-[12.5px] bg-white/[0.03] rounded-md px-2.5 py-2">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-accent-300 flex-shrink-0">
                <path d="m22 2-7 20-4-9-9-4Z" />
                <path d="M22 2 11 13" />
              </svg>
              <span className="text-slate-400">Telegram chat</span>
              <span className="font-mono text-slate-100 truncate" title={chatId ?? undefined}>
                {chatId}
              </span>
              <button
                type="button"
                onClick={onChangeChat}
                className="ml-auto text-[12px] font-medium text-accent-300 hover:text-accent-200 whitespace-nowrap"
              >
                Change
              </button>
            </div>
          )}
        </div>
        {err && <div className="text-[12px] text-rose-300">{err}</div>}
        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose} className="btn-secondary" disabled={busy}>
            Cancel
          </button>
          <button
            onClick={save}
            disabled={busy}
            className="h-9 px-4 rounded-lg text-[13px] font-semibold bg-accent-600 text-white hover:bg-accent-500 disabled:opacity-50 transition"
          >
            {busy ? "Saving…" : editing ? "Save" : "Create alert"}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Set or change the user's one Telegram chat. Saving rewrites every existing
 *  alert to the new chat too, so "my alerts go here" stays literally true.
 *  Sits above the alert dialog (z-1400) because it can be opened from it. */
function TelegramChatModal({
  current,
  botUsername,
  botEnabled,
  alertCount,
  onClose,
  onSaved,
}: {
  current: string | null;
  botUsername: string | null;
  botEnabled: boolean;
  alertCount: number;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [value, setValue] = useState(current ?? "");
  const [busy, setBusy] = useState<"save" | "test" | null>(null);
  const [msg, setMsg] = useState<{ kind: "ok" | "err"; text: string } | null>(null);
  const trimmed = value.trim();
  const changed = trimmed !== (current ?? "");

  async function test() {
    if (!trimmed) {
      setMsg({ kind: "err", text: "Enter a chat id first." });
      return;
    }
    setBusy("test");
    setMsg(null);
    try {
      await api("/api/fresh/alerts/telegram/test", {
        method: "POST",
        json: { telegram_chat_id: trimmed },
      });
      setMsg({ kind: "ok", text: "Test message sent — check Telegram." });
    } catch (e) {
      setMsg({
        kind: "err",
        text:
          e instanceof ApiError && e.status === 502
            ? "Couldn't reach that chat. Make sure you pressed Start on the bot and the id is right."
            : e instanceof ApiError
              ? e.message
              : "Test failed.",
      });
    } finally {
      setBusy(null);
    }
  }

  async function save() {
    if (!trimmed) {
      setMsg({ kind: "err", text: "Enter a chat id." });
      return;
    }
    setBusy("save");
    setMsg(null);
    try {
      await api("/api/fresh/alerts/telegram", {
        method: "PUT",
        json: { telegram_chat_id: trimmed },
      });
      onSaved();
    } catch (e) {
      setMsg({ kind: "err", text: e instanceof ApiError ? e.message : "Save failed." });
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="fixed inset-0 z-[1400] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-md card p-5 space-y-4">
        <div>
          <div className="text-sm font-semibold text-slate-100">
            {current ? "Change Telegram chat" : "Set up Telegram alerts"}
          </div>
          <div className="text-[12px] text-slate-500 mt-0.5">
            One chat for all your fresh-listing alerts.
          </div>
        </div>
        {!botEnabled && (
          <div className="text-[11.5px] text-amber-300/90 bg-amber-500/10 border border-amber-500/20 rounded-md px-2.5 py-1.5">
            The alert bot isn't configured on the server yet — you can save the id now, but
            tests and alerts won't deliver until it is.
          </div>
        )}
        <TelegramHowTo botUsername={botUsername} />
        <div className="space-y-1">
          <label className="text-[11px] uppercase tracking-wider text-slate-500">
            Telegram chat id
          </label>
          <input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="e.g. 123456789"
            className="input font-mono"
            inputMode="numeric"
            autoFocus
          />
          {current && changed && alertCount > 0 && (
            <div className="text-[11px] text-slate-500">
              Your {alertCount} existing alert{alertCount === 1 ? "" : "s"} will switch to this
              chat too.
            </div>
          )}
        </div>
        {msg && (
          <div className={`text-[12px] ${msg.kind === "ok" ? "text-emerald-300" : "text-rose-300"}`}>
            {msg.text}
          </div>
        )}
        <div className="flex items-center gap-2 pt-1">
          <button
            type="button"
            onClick={test}
            disabled={busy !== null || !trimmed}
            className="h-9 px-3 rounded-lg text-[12.5px] font-medium text-slate-300 border border-white/10 hover:border-white/20 hover:text-slate-100 disabled:opacity-50 transition"
          >
            {busy === "test" ? "Sending…" : "Send test"}
          </button>
          <div className="ml-auto flex gap-2">
            <button onClick={onClose} className="btn-secondary" disabled={busy !== null}>
              Cancel
            </button>
            <button
              onClick={save}
              disabled={busy !== null || !trimmed || (!changed && current != null)}
              className="h-9 px-4 rounded-lg text-[13px] font-semibold bg-accent-600 text-white hover:bg-accent-500 disabled:opacity-50 transition"
            >
              {busy === "save" ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
